import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import GoogleMap from "google-map-react";
import classNames from "classnames";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import AttachmentListItem from "@components/common/AttachmentListItem";
import utils from "@utils";
import AnalyticsService from "@services/AnalyticsService";
import { ANALYTICS_EVENTS_ACTIONS, ANALYTICS_EVENTS_CATEGORIES } from "@constants/analyticsEvents";
import {
	JOB_ASSIGNMENT_ERROR_TYPES,
	JOB_TYPES,
	JOB_POSTED_TYPES,
	OFFICER_STATUSES,
	AUTO_APPROVAL_TYPES,
	GOOGLE_MAP_SETTINGS,
	GOOGLE_MAP_OPTIONS,
	FORM_JOB_TYPES,
	JOB_ESTIMATE_APPROVAL_STATUS,
	JOB_INVOICED_TYPES,
	NON_RK_PAY_INVOICE_STATUS_V4,
} from "@constants";

import {
	JobService,
	NoteService,
	ApplicantService,
	PayCodesService,
	TimecardService,
} from "@services";
import AddToEstimateModal from "../../payments/AddToEstimate/AddToEstimateModal";
import AddToInvoiceModal from "../../payments/AddToInvoicie/AddToInvoiceModal";
import ConnectedInvoicesModal from "../../payments/invoices/components/ConnectedInvoicesModal/ConnectedInvoicesModal";
import {
	ConfirmationModal,
	ManualAssignModal,
	UnassignOfficerModal,
	PostJobModal,
	SeriesConfirmationModal,
	Loader,
	Tooltip,
	Image,
	MapMarker,
} from "../../../components";
import {
	AddNoteModal,
	JobActivityLogModal,
	JobHistoryModal,
	EditTimecardModal,
	ViewNotesModal,
	DeleteJobConfirmationModal,
} from "./components";

import "./JobDetails.scss";

class JobDetails extends Component {
	constructor(props) {
		super(props);
		this.initialUnassignModal = {
			positionId: "",
			officerId: undefined,
			officerName: undefined,
			show: false,
			recurringAssignment: false,
			recurrenceRule: undefined,
		};

		this.initialSeriesConfirmationModal = {
			show: false,
			actionType: "",
			onConfirm: () => {},
		};

		this.state = {
			actionPermissions: {},
			showRosterView: false,
			showNotes: false,
			isLoading: true,
			notesLoading: false,
			jobEvent: {},
			positionManager: {},
			jobEventNotes: [],
			searchQuery: {},
			applicantList: {},
			assignedOfficersList: {},
			manualAssignModal: { show: false, positionId: undefined },
			unassignModal: { ...this.initialUnassignModal },
			seriesConfirmationModal: { ...this.initialSeriesConfirmationModal },
			showAddNoteModal: false,
			showJobHistoryModal: false,
			timecardsToEdit: [],
			showViewNotesModal: false,
			showEditTimecardModal: false,
			showJobActivityLogModal: false,
			showConnectedInvoicesModal: false,
			clockinTimecardModal: "",
			clockoutTimecardModal: "",
			currentOfficerClockIn: "",
			currentOfficerClockOut: "",
			currentPositionEditId: "",
			currentOfficerEdit: undefined,
			currentPositionView: undefined,
			currentOfficerView: undefined,
			notesTimecardModal: "",
			postJobModal: { show: false, updateAllUpcoming: false },
			deleteJobConfirmationModal: { show: false, deleteAll: false, loading: false },
			agencyPayCodes: [],
			agencyPTAEOCodes: [],
			associatedNonRKPayInvoices: [],
			associatedNonRKPayInvoiceEstimate: null,
			allowEditTimeCard: false,
			showAddToEstimateModal: false,
			showAddToInvoiceModal: false,
		};

		const {
			flags: {
				nonrkpayDetailPrepayEnhancements,
				jobDescriptionAttachments,
				relocateJobFormAddToInvoicingV3Button,
			},
		} = props;

		this.initialState = { ...this.state };
		this.timeMask = [/[0-1]/, /[0-9]/, ":", /[0-5]/, /[0-9]/, " ", /(A|P)/i, /M/i];
		this._confirmationModal = React.createRef();
		this._jobHistoryModal = React.createRef();

		this.hasNonrkpayDetailPrepayEnhancements = !!nonrkpayDetailPrepayEnhancements;
		this.jobDescriptionAttachments = !!jobDescriptionAttachments;
		this.relocateJobFormAddToInvoicingV3Button = !!relocateJobFormAddToInvoicingV3Button;

		if (this.jobDescriptionAttachments) {
			this.state.additionalInformation = {};
		}
	}

	componentDidMount() {
		const { lang } = global;
		const {
			params: { jobId },
			user,
			initializeChat,
			router: { push },
		} = this.props;
		if (window.location.href.includes("#openChat")) {
			initializeChat(user, pubnub => {
				// Trigger a subscription action to either become a member of this chat or open the chat window to this job automatically
				pubnub.subscribe({ channels: [`job-${jobId}`] });
				// Remove #openChat from the url so that the chat won't constantly open on refresh
				push(`/jobs/${jobId}`);
			});
		}
		this.receiveJobEvent(jobId);
		if (user.Organization.NonRKPayInvoicingEnabled) {
			JobService.getJobEventNonRKPayInvoices(jobId)
				.then(({ data }) => {
					this.setJobEventNonRKPayInvoices(data);
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message: lang.t("Error getting Job Invoices"),
						title: "Error",
					});
				});
		}
		document.addEventListener("click", this.clickEventListener);
	}

	componentDidUpdate({ params: prevParams }) {
		const { jobId: prevId } = prevParams;
		const {
			params: { jobId },
		} = this.props;
		// check if jobId parameter has changed since notifications can route to this page
		if (prevId !== jobId) {
			// eslint-disable-next-line react/no-did-update-set-state
			this.setState(this.initialState, () => this.receiveJobEvent(jobId));
		}
	}

	componentWillUnmount() {
		this.setState({ showRosterView: false, isLoading: false });
		document.removeEventListener("click", this.clickEventListener);
	}

	clickEventListener = ({ target }) => {
		const dropdown = document.getElementById("action-menu");
		if (dropdown && dropdown.classList.contains("open") && !target.closest("#action-menu")) {
			dropdown.classList.remove("open");
		}
	};

	/** Sets what actions are permitted for the current job event */
	getActionPermissions = () => {
		const { user } = this.props;
		const { jobEvent } = this.state;
		this.setState({
			actionPermissions: utils.getJobActionPermissions(user, jobEvent),
		});
	};

	/** Retrieves details for the current job event from the API. Because the current API structure does not return modified objects as part of responses,
	 * This method is called after almost any update to the job event is made.
	 * @param {Number} jobId The ID number for the job event
	 */
	receiveJobEvent = async jobId => {
		const { lang } = global;
		const { positionManager } = this.state;
		const { router, user } = this.props;

		try {
			this.getPayCodes();
			const { data } = await JobService.getById(jobId);

			data.Jobs.forEach(job => {
				if (!positionManager[job.Id]) {
					positionManager[job.Id] = {
						officersRetrieved: false,
						expanded: false,
						showAssigned: true,
						showDetails: false,
						showSeeking: false,
					};
				}
				// eslint-disable-next-line no-param-reassign
				job._positionStartDate = utils.formatDate(
					new Date(`${job.StartDateTime}Z`),
					"en-US",
					user.TimeZoneId,
					"2-digit",
				);
			});

			// Update state with job event data
			await new Promise(resolve => {
				this.setState(
					prevState => ({
						jobEvent: { ...prevState.jobEvent, ...data },
						positionManager,
					}),
					resolve,
				);
			});

			// Get action permissions and notes after state has been updated
			this.getActionPermissions();
			await this.getJobEventNotes();
		} catch (error) {
			utils.notify({
				type: "error",
				message: lang.t("Error loading Job"),
				title: "Error",
			});
			router.push("/jobs");
		}

		try {
			if (this.jobDescriptionAttachments) {
				const { data: additionalInformation } =
					await JobService.getAdditionalInformationById(jobId);
				this.setState({ additionalInformation });
			}
		} catch (error) {
			utils.notify({
				type: "error",
				message: lang.t("Error loading job additional information"),
				title: "Error",
			});
		} finally {
			/**
			 * Mark loading as complete
			 * whether the job event additional
			 * information was loaded successfully or not
			 * */
			this.setState({ isLoading: false });
		}
	};

	/**
	 * Restores a deleted job event
	 * @param {Boolean} restoreAll `true` if all future deleted job events in a series should be restored, otherwise `false`
	 */
	handleRestoreJob = restoreAll => {
		const { lang } = global;
		const {
			jobEvent: { Id: id },
		} = this.state;

		JobService.restore({ id, restoreAll })
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Job restored successfully"),
					title: "Success",
				});
				this.receiveJobEvent(id);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error restoring Job"),
					title: "Oops",
				});
			});
	};

	setJobEventNonRKPayInvoices = data => {
		if (data.length === 0) return;

		const estimate = data.find(est => est.IsEstimate) || null;
		const invoices = data.filter(inv => !inv.IsEstimate);

		this.setState({
			associatedNonRKPayInvoiceEstimate: estimate,
			associatedNonRKPayInvoices: invoices,
		});
	};

	/** Retrieves all notes pertaining to the current job event */
	getJobEventNotes = () => {
		const { lang } = global;
		this.setState({ notesLoading: true });
		const {
			jobEvent: { Id },
		} = this.state;

		NoteService.getAll({
			displayCount: 10,
			jobEventId: Id,
		})
			.then(({ data }) => {
				this.setState({
					jobEventNotes: data.ItemList,
					notesLoading: false,
				});
			})
			.catch(() => {
				this.setState({ notesLoading: false });
				utils.notify({
					type: "error",
					message: lang.t("Error getting notes"),
					title: "Error",
				});
			});
	};

	/** Deletes a note associated with the current job event
	 * @param {Number} id The ID number for the note
	 */
	handleDeleteJobEventNote = id => {
		const { lang } = global;
		this.setState({ notesLoading: true });
		NoteService.delete({ id })
			.then(() => {
				utils.notify({
					type: "success",
					message: "Note successfully deleted",
					title: "Success",
				});
				this.setState(prevState => ({
					notesLoading: false,
					jobEventNotes: prevState.jobEventNotes.filter(note => note.Id !== id),
				}));
			})
			.catch(() => {
				this.setState({ notesLoading: false });
				utils.notify({
					type: "error",
					message: lang.t("Error deleting Note"),
					title: "Error",
				});
			});
	};

	openManualAssignModal = positionId => {
		const {
			jobEvent: { Jobs },
		} = this.state;
		this.setState({ manualAssignModal: { show: true, positionId: positionId || Jobs[0].Id } });
	};

	/**
	 * Opens the post job modal for a single job event. If the job event is part of a series,
	 * a confirmation modal is shown instead ({@link openSeriesConfirmationModal}), with the confirm action opening the post job modal
	 * @param {Event} event The HTML event handled by this method
	 */
	onPostJob = event => {
		event.preventDefault();

		const {
			jobEvent: { Posted, JobEventRecurrenceRuleId },
		} = this.state;

		if (JobEventRecurrenceRuleId && JobEventRecurrenceRuleId !== null) {
			this.openSeriesConfirmationModal(Posted ? "unpost" : "post", updateAll =>
				this.openPostjobModal(updateAll),
			);
			return;
		}

		this.openPostjobModal(false);
	};

	/**
	 * Opens a confirmation modal that asks whether updates should be made for an entire job series or a single job event
	 * @param {'post'|'unpost'|'restore'|'delete'} actionType The action to prompt the user about
	 * @param {Function} onConfirm A method to call upon receiving user confirmation
	 */
	openSeriesConfirmationModal = (actionType, onConfirm) => {
		this.setState({
			seriesConfirmationModal: {
				show: true,
				onConfirm,
				actionType,
			},
		});
	};

	closeSeriesConfirmationModal = () => {
		this.setState({ seriesConfirmationModal: { ...this.initialSeriesConfirmationModal } });
	};

	openPostjobModal = (updateAllUpcoming = false) => {
		const { lang } = global;
		const {
			jobEvent: { Posted, Id },
		} = this.state;

		/* If the job is already posted we need to unPost it, but we don't need to confirm anything else,
		 * so we don't need to show the the postJobModal */
		if (Posted) {
			JobService.postJob({ updateAllUpcoming, id: Id }, { Posted: false })
				.then(() => {
					this.setState(
						{ postJobModal: { show: false, updateAllUpcoming: false } },
						() => {
							this.receiveJobEvent(Id);
						},
					);
					utils.notify({
						type: "success",
						message: lang.t("Job unposted successfully"),
					});
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message: lang.t("Error posting Job"),
						title: "Error",
					});
				});
			return;
		}

		this.setState({ postJobModal: { show: true, updateAllUpcoming } });
	};

	/**
	 * Opens the appropriate confirmation modals to verify that the user would like to restore a job(s)
	 * @param {Event} event The HTML event handled by this method
	 */
	onRestoreJob = event => {
		event.preventDefault();
		const { lang } = global;
		const {
			jobEvent: { JobEventRecurrenceRuleId },
		} = this.state;

		if (JobEventRecurrenceRuleId && JobEventRecurrenceRuleId !== null) {
			// This job is part of a recurrent set ( Confirm the action )
			const onConfirm = restoreAll => {
				this._confirmationModal.openModal({
					title: lang.t("Restore Jobs"),
					message: lang.t("dynamic.Are you sure you want to restore", {
						subject: lang.t("these jobs"),
					}),
					confirmButton: `${lang.t("Yes")}, ${lang.t("Restore Jobs")}`,
					onConfirm: () => this.handleRestoreJob(restoreAll),
				});
			};
			this.openSeriesConfirmationModal("restore", onConfirm);
			return;
		}

		this._confirmationModal.openModal({
			title: lang.t("Restore Job"),
			message: lang.t("dynamic.Are you sure you want to restore", {
				subject: lang.t("this job"),
			}),
			confirmButton: `${lang.t("Yes")}, ${lang.t("Restore Job")}`,
			onConfirm: () => this.handleRestoreJob(),
		});
	};

	onOpenHistoryModal = event => {
		event.preventDefault();
		const {
			jobEvent: { Id },
		} = this.state;

		this._jobHistoryModal.openModal(Id);
	};

	/**
	 * Opens the appropriate confirmation modals to verify that the user would like to delete a job(s)
	 * @param {Event} event The HTML event handled by this method
	 */
	onDeleteJob = event => {
		event.preventDefault();

		const {
			jobEvent: { JobEventRecurrenceRuleId },
		} = this.state;

		if (JobEventRecurrenceRuleId && JobEventRecurrenceRuleId !== null) {
			// This job is part of a recurrent set ( Confirm the action )
			const onConfirm = deleteAll => {
				this.setState({
					deleteJobConfirmationModal: { show: true, deleteAll, loading: false },
				});
			};
			this.openSeriesConfirmationModal("delete", onConfirm);
			return;
		}

		this.setState({
			deleteJobConfirmationModal: { show: true, deleteAll: false, loading: false },
		});
	};

	/**
	 * Deletes the current job event
	 * @param {Object} obj
	 * @param {Boolean} obj.deleteAll Deletes all upcoming job events in the current job event's series if `true`
	 * @param {Number} obj.id The ID number of a job event to delete. The ID number of the current job event is taken if none is provided
	 * @param {String} obj.reasonNote The reason for deleting the job event
	 */
	handleDeleteJob = (deleteReason = "") => {
		const { lang } = global;
		const { router } = this.props;
		const {
			jobEvent,
			deleteJobConfirmationModal: { deleteAll },
		} = this.state;

		this.setState(prevState => ({
			deleteJobConfirmationModal: {
				...prevState.deleteJobConfirmationModal,
				loading: true,
			},
		}));
		JobService.deleteJob(
			{},
			{ deleteAllUpcoming: deleteAll, jobEventId: jobEvent.Id, deleteReason },
		)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Job deleted successfully"),
					timeOut: 1500,
				});
				this.setState(
					{
						deleteJobConfirmationModal: {
							show: false,
							deleteAll: false,
							loading: false,
						},
					},
					() => {
						const onNavigate = setTimeout(() => {
							router.push("/jobs");
							clearTimeout(onNavigate);
						}, 500);
					},
				);
			})
			.catch(error => {
				this.setState({
					deleteJobConfirmationModal: { show: false, deleteAll: false, loading: false },
				});
				const { response } = error;
				utils.notify({
					type: "error",
					message: lang.t(response.data.Message),
					timeOut: 1500,
				});
			});
	};

	/**
	 * Handles changes to applicant status such as accepting/rejecting an application to work a job, or
	 * unassigning an officer from a job entirely.
	 * @param {Object} data Can either be officer data or applicant status data, as defined by the parameter `isOfficerData`
	 * @param {Number} positionId The ID number for a position being updated
	 * @param {Boolean} isOfficerData Indicates whether the data provided in the first parameter is raw officer information or not. `false` by default.
	 */
	onChangeApplicantStatus =
		(data, positionId, isOfficerData = false) =>
		() => {
			let params = data;
			if (isOfficerData) {
				const {
					jobEvent: { JobEventRecurrenceRule },
				} = this.state;
				params = {
					isExternalOfficer: data.isExternal || false,
					officerId: data.isExternal ? data.Id : data.UserId,
					officerName: `${data.OfficerProfile?.FirstName || ""} ${data.OfficerProfile?.LasttName || ""}`,
					isPermanent:
						(data.RecurrenceRuleJobAssignment &&
							data.RecurrenceRuleJobAssignment !== null,
						JobEventRecurrenceRule),
					type: "unAssign",
				};
			}
			this.onUpdatePosition({
				...params,
				positionId,
			});
		};

	/**
	 * @param {Object} applicant Applicant data used to determine whether their application to work the job is pending or not
	 * @returns {Boolean} `true` if application is pending approval, `false` otherwise
	 */
	isApplicationPending = applicant =>
		applicant.ApprovedDateTimeUTC === null &&
		applicant.DenialDateTimeUTC === null &&
		!applicant.Deleted;

	/**
	 * @todo Replace this function with {@link isApplicationPending}
	 */
	displayActionButtons = applicant => this.isApplicationPending(applicant);

	/**
	 * Updates position data based on latest change to applicant status. Only called by {@link onChangeApplicantStatus}.
	 * @param {Object} obj
	 * @param {'accept'|'decline'|'unAssign'} obj.type
	 * @param {Number} obj.positionId
	 * @param {Number} obj.officerId
	 * @param {Number} obj.userId
	 * @param {String} obj.officerName
	 * @param {Boolean} obj.isExternalOfficer
	 * @param {Boolean} obj.isPermanent
	 * @param {Object} obj.recurrenceData
	 */
	onUpdatePosition = ({
		type,
		positionId,
		officerId,
		userId,
		officerName,
		isExternalOfficer,
		isPermanent,
		recurrenceData,
	}) => {
		// possible refactor unneded switch???
		switch (type) {
			case "accept":
			case "decline":
				this.updateApplication(positionId, userId, type === "accept");
				break;

			case "unAssign": {
				// If it's external officer, we don't need to show the unAssign modal
				if (isExternalOfficer) {
					this.unAssignExternalOfficer(positionId, officerId);
					break;
				}

				this.setState({
					unassignModal: {
						show: true,
						recurringAssignment: !!isPermanent,
						recurrenceRule: recurrenceData,
						positionId,
						officerId,
						officerName,
					},
				});
				break;
			}

			default:
				break;
		}
	};

	/**
	 * Unassigns an non-RollKall officer from a position and updates the job details with {@link receiveJobEvent}
	 * @param {Number} positionId The ID number for the position being updated. Used to update officer list in place.
	 * @param {Number} userId The ID number for an external officer
	 */
	unAssignExternalOfficer = (positionId, userId) => {
		const { lang } = global;
		const {
			jobEvent: { Id },
		} = this.state;

		ApplicantService.unAssignExternalOfficer(userId)
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Officer unassigned successfully"),
					title: "Success",
				});
				// We cannot update values in place because the actionPermissions (allowAssign, allowDelete, etc) only
				// update properly by analyzing accurate jobEvent values
				this.receiveJobEvent(Id);
				this.retrieveOfficersForPosition(positionId);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error unassigning Officer"),
					title: "Error",
				});
			});
	};

	/**
	 * Update the status of an officer's application tp work a position on a job event
	 * @param {Number} jobId The ID number of the position to update
	 * @param {Number} userId The user ID number of the applicant
	 * @param {Boolean} accept Accepts the application when `true`, declines when `false`
	 */
	updateApplication = (jobId, userId, accept = true) => {
		const { lang } = global;
		// Start loading animation
		const {
			jobEvent: { Id: id },
		} = this.state;

		ApplicantService.update({
			accept,
			jobId,
			userId,
		})
			.then(() => {
				/** If the applicants has been accepted we also need to reload assigned officer data */
				if (accept) {
					if (id) this.receiveJobEvent(id);
				}
				// Reload applicants
				this.retrieveOfficersForPosition(jobId);

				// Notify user
				utils.notify({
					type: "success",
					message: accept
						? lang.t("Application Accepted successfully")
						: lang.t("Application Denied successfully"),
				});
			})
			.catch(error => {
				utils.notify({
					type: "error",
					message: error.response?.data?.Message ?? lang.t("Error loading Applicants"),
					title: "Error",
				});
			});
	};

	/**
	 * Accepts an officer application
	 * @param {Object} applicant Applicant data
	 * @param {Boolean} ignoreMaxWeeklyHrs flag to ignore max allowed hours
	 */
	acceptApplication = (applicant, ignoreMaxWeeklyHrs = false) => {
		const { lang } = global;
		const {
			jobEvent: { Id: id },
		} = this.state;

		ApplicantService.update(
			{
				accept: true,
				jobId: applicant.JobId,
				userId: applicant.UserId,
			},
			{ ignoreMaxWeeklyHrs },
		)
			.then(() => {
				if (id) this.receiveJobEvent(id);
				// Reload applicants
				this.retrieveOfficersForPosition(applicant.JobId);

				// Notify user
				utils.notify({
					type: "success",
					message: lang.t("Application Accepted successfully"),
				});
			})
			.catch(({ response: { status, data } }) => {
				if (
					status === 409 &&
					(data.ConflictType ===
						JOB_ASSIGNMENT_ERROR_TYPES.MAX_WEEKLY_WORK_HOUR_REACHED.value ||
						data.ConflictType ===
							JOB_ASSIGNMENT_ERROR_TYPES.MAX_HOURS_REACHED_BASED_ON_NEW_RULES.value)
				) {
					this.onOfficerHoursLimitReached(applicant, data?.Message);
				} else {
					utils.notify({
						type: "error",
						message: data?.Message ?? lang.t("Error updating applicant status"),
						title: "Error",
					});
				}
			});
	};

	/**
	 * Displays a modal indicating that an officer has reached their off-duty hour limit. If the officer belongs to the same jurisdiction
	 * as the current user and is a LEA Admin or Super Admin they will be asked if they would like to override this rule. Otherwise, they are simply informed
	 * of the conflict and cannot proceed further.
	 * @param {Object} applicant Officer information
	 * @param {String} message Error message to display
	 */
	onOfficerHoursLimitReached = (applicant, message) => {
		const { lang } = global;
		const { user } = this.props;
		const isSuperAdmin = user?.isSuperAdmin();
		const isLEA = user?.isLEA();
		const isStaffAdmin = !!user?.IsStaffAdmin;
		const isDifferentJurisdiction =
			applicant?.User?.JurisdictionId !== user?.Organization?.JurisdictionId;
		const canOverrideHourlyLimits =
			!isDifferentJurisdiction && (isSuperAdmin || (isLEA && isStaffAdmin));

		this._confirmationModal.openModal({
			title: lang.t("Hourly Limits Exceeded"),
			message:
				message ||
				lang.t(
					"Assigning this officer to this job will exceed their allowable number of hours_",
				),
			hideCancel: !canOverrideHourlyLimits,
			confirmButton: canOverrideHourlyLimits ? lang.t("Proceed") : lang.t("Cancel"),
			onCancel: () => {},
			onConfirm: () => {
				if (!canOverrideHourlyLimits) return;
				this.acceptApplication(applicant, true);
			},
		});
	};

	/** Renders the job event date, adding labels for flex jobs and local time listing if needed */
	renderEventDate = () => {
		const {
			jobEvent: { JobEventTypeId, StartDateTime, EndDateTime, TimeZoneId, Jobs = [] },
		} = this.state;
		const {
			user: { TimeZoneId: userTimeZone, UserPreferences },
		} = this.props;
		const { lang } = global;
		const jobTimeZoneId = TimeZoneId || userTimeZone;

		const startDateF = utils.formatDate(
			new Date(`${StartDateTime}Z`),
			"en-US",
			jobTimeZoneId,
			"2-digit",
		);
		const endDateF = utils.formatDate(
			new Date(`${EndDateTime}Z`),
			"en-US",
			jobTimeZoneId,
			"2-digit",
		);
		const isJobEvent = JobEventTypeId === FORM_JOB_TYPES.EVENT.value && startDateF !== endDateF;

		return (
			<Fragment>
				{startDateF}
				<If condition={isJobEvent}>
					&nbsp;-&nbsp;
					{endDateF}
				</If>
				<br />
				{utils.formatTime(
					new Date(`${StartDateTime}Z`),
					UserPreferences !== "useMilitaryTime",
					jobTimeZoneId,
				)}
				&nbsp;-&nbsp;
				{utils.formatTime(
					new Date(`${EndDateTime}Z`),
					UserPreferences !== "useMilitaryTime",
					jobTimeZoneId,
				)}
				<If condition={Jobs.some(pos => pos.FlexJob)}>
					&nbsp;<span className="label label-info">{lang.t("FLEX")}</span>
				</If>
				{/* Only display date in user time zone, if it's different than job userTimezone */}
				<If condition={userTimeZone !== jobTimeZoneId}>
					<br />(
					{utils.formatTime(
						new Date(`${StartDateTime}Z`),
						UserPreferences !== "useMilitaryTime",
						userTimeZone,
					)}
					&nbsp;-&nbsp;
					{utils.formatTime(
						new Date(`${EndDateTime}Z`),
						UserPreferences !== "useMilitaryTime",
						userTimeZone,
					)}
					&nbsp;
					{lang.t("local time")})
				</If>
			</Fragment>
		);
	};

	/** Constructs and renders the posted status of the job */
	renderPostedStatus = () => {
		const { lang } = global;
		const {
			jobEvent: {
				Posted,
				PostedType,
				JobAutoAssignType,
				PostedDate,
				PostAtDateTime,
				PostedGroupName,
				JobEventInvoicEstimateApproval,
				AssignmentGroupName,
				RequireClientInvoiceEstimateApproval,
				AllowJobApplicationsWhileActive,
			},
		} = this.state;
		const {
			user: { TimeZoneId: userTimeZone, UserPreferences },
		} = this.props;
		let postedStatus = "Not Posted";
		let autoAssignType = "None";
		const now = new Date();

		if (
			Posted &&
			(PostedType === JOB_POSTED_TYPES.ALL ||
				PostedType === JOB_POSTED_TYPES.ALL_MINUTES_CONSTRAINT ||
				PostedType === JOB_POSTED_TYPES.ALL_WEEKDAY_CONSTRAINT)
		) {
			postedStatus =
				now > new Date(`${PostAtDateTime || PostedDate}Z`)
					? lang.t("Posted")
					: lang.t("Will be posted on");
		}

		if (Posted && PostedType === JOB_POSTED_TYPES.ALL_GROUPS) {
			postedStatus =
				now > new Date(`${PostAtDateTime || PostedDate}Z`)
					? lang.t("Posted to All Officer Groups")
					: lang.t("Will be posted to All Officer Groups on");
		}

		if (Posted && PostedType === JOB_POSTED_TYPES.GROUPS) {
			postedStatus =
				now > new Date(`${PostAtDateTime || PostedDate}Z`)
					? lang.t("dynamic.Posted to group", { PostedGroupName })
					: lang.t("dynamic.Will be posted to group", { PostedGroupName });
		}

		if (Posted) {
			if (AssignmentGroupName !== null) {
				autoAssignType = `${lang.t("Assignment Rules")}: ${AssignmentGroupName}`;
			} else {
				switch (JobAutoAssignType) {
					case AUTO_APPROVAL_TYPES.FIRST_COME_FIRST_SERVED:
						autoAssignType = lang.t("Auto-Assign: First-come, first-serve");
						break;
					case AUTO_APPROVAL_TYPES.BY_HOURS:
						autoAssignType = lang.t("Auto-Assign: Number of hours");
						break;
					case AUTO_APPROVAL_TYPES.BY_JOBS:
						autoAssignType = lang.t("Auto-Assign: Number of jobs");
						break;
					case AUTO_APPROVAL_TYPES.SENIORITY_DESCENDING:
						autoAssignType = lang.t("Auto-Assign: By Descending Seniority");
						break;
					case AUTO_APPROVAL_TYPES.SENIORITY_ASCENDING:
						autoAssignType = lang.t("Auto-Assign: By Ascending Seniority");
						break;
					case AUTO_APPROVAL_TYPES.RANDOM:
						autoAssignType = "Random Officer Auto-Assign";
						break;
					default:
						autoAssignType = "";
						break;
				}
			}

			if (
				JobEventInvoicEstimateApproval &&
				JobEventInvoicEstimateApproval.Status ===
					JOB_ESTIMATE_APPROVAL_STATUS.PENDING.value &&
				RequireClientInvoiceEstimateApproval
			) {
				postedStatus += `, ${lang.t("Pending Approval")}`;
			}
		}

		if (PostAtDateTime || PostedDate) {
			const space = now.getTime() > new Date(`${PostAtDateTime}Z`).getTime() ? ", " : " ";
			const date = utils.formatDate(
				new Date(`${PostAtDateTime || PostedDate}Z`),
				"en-US",
				userTimeZone,
				"2-digit",
			);
			const time = utils.formatTime(
				new Date(`${PostAtDateTime || PostedDate}Z`),
				UserPreferences !== "useMilitaryTime",
				userTimeZone,
			);
			postedStatus += `${space}${date} ${time}`;
		}

		return (
			<Fragment>
				{postedStatus}
				<If condition={Posted && autoAssignType}>
					<br />
					{autoAssignType}
				</If>
				<If condition={Posted && AllowJobApplicationsWhileActive}>
					<br />
					{lang.t("Open while active")}
				</If>
			</Fragment>
		);
	};

	/**
	 * Renders required and excluded jurisdictions for the job event
	 * @param {Object} jurisdiction
	 * @param {Number} jurisdiction.Id The ID number for the jurisdiction
	 * @param {String} jurisdiction.Name The name of the jurisdiction
	 * @param {Number} jurisdiction.ExpiresBefore How long before the job starts to enforce this rule
	 */
	renderJurisdictionRestriction = jurisdiction => {
		const { lang } = global;
		const {
			jobEvent: { StartDateTime },
		} = this.state;
		const { JurisdictionName, ExpiresBefore } = jurisdiction;
		if (ExpiresBefore === null) return JurisdictionName;

		const startDate = new Date(`${StartDateTime}Z`);
		const expirationDate = new Date(startDate.getTime() - ExpiresBefore * 86400000);
		const expiresIn = {
			days: (expirationDate - Date.now()) / 86400000,
			hours: (expirationDate - Date.now()) / 3600000,
			minutes: (expirationDate - Date.now()) / 60000,
		};
		if (expiresIn.days <= 0) return `${JurisdictionName} (${lang.t("restriction expired")})`;
		if (expiresIn.minutes <= 1) return `${JurisdictionName} (${lang.t("expires soon")})`;
		/* eslint-disable camelcase */
		if (expiresIn.hours <= 1)
			return `${JurisdictionName} (${lang.t("dynamic.expires in minutes", { smart_count: Math.ceil(expiresIn.minutes) })})`;
		if (expiresIn.days <= 1)
			return `${JurisdictionName} (${lang.t("dynamic.expires in hours", { smart_count: Math.ceil(expiresIn.hours) })})`;
		return `${JurisdictionName} (${lang.t("dynamic.expires in days", { smart_count: Math.ceil(expiresIn.days) })})`;
		/* eslint-enable camelcase */
	};

	/** Renders a `string` indicating additional agency contacts for a job */
	renderAdditionalContacts = () => {
		const {
			jobEvent: { SecondarySupervisorModel },
		} = this.state;

		let supervisors = "";
		const arrayLength = SecondarySupervisorModel.length;

		SecondarySupervisorModel.forEach((item, index) => {
			const newName = `${item.SecondaryAgencySupervisorName}${index !== arrayLength - 1 ? ", " : ""}`;
			supervisors += newName;
		});

		return supervisors;
	};

	/**
	 * Renders a `string` indicating required certifications for a position
	 * @param {Number} positionId The ID number for the position
	 */
	renderCertifications = positionId => {
		const {
			jobEvent: { Jobs },
		} = this.state;
		const position = Jobs.find(job => job.Id === positionId);
		const arrayLength = position.JobCertifications.length;

		let certifications = "";
		position.JobCertifications.forEach((cert, index) => {
			certifications += `${cert.OfficerCertificationTypeName}${index !== arrayLength - 1 ? ", " : ""}`;
		});

		return certifications;
	};

	/** Renders a `string` indicating the officer pay rate or range of pay rate for a job event */
	renderOfficerRate = () => {
		const {
			jobEvent: { MinRate = 0, MaxRate = 0 },
		} = this.state;
		if (MinRate === MaxRate) return utils.currencyFormatter(MinRate);
		return `${utils.currencyFormatter(MinRate)} - ${utils.currencyFormatter(MaxRate)}`;
	};

	/**
	 * Renders a string to display a properly formatted address
	 * @todo Remove reliance on a `utils` function for this
	 */
	renderLocationDetails = () => {
		const {
			jobEvent: {
				LocationLatitude: latitude,
				LocationLongitude: longitude,
				LocationAddress: address,
				LocationCity: city,
				LocationState: state,
				LocationZipcode: zip,
			},
		} = this.state;

		return utils.concatenateAddressDetails({
			address,
			city,
			state,
			zip,
			latitude,
			longitude,
		});
	};

	/**
	 * Renders a label to visually indicate an officer's status during the duration of the job
	 * Status descriptions can be found in the `OFFICER_STATUSES` constant.
	 * @param {Number} type An ID number indicating an officer's status
	 */
	renderOfficerStatus = type => {
		const officerStatus =
			Object.values(OFFICER_STATUSES).find(status => status.type === type) ||
			OFFICER_STATUSES.none;
		return (
			<If condition={officerStatus.type !== OFFICER_STATUSES.none.type}>
				<div
					className="col-xs-6 col-sm-12"
					title={officerStatus.description}
				>
					<div className={`officer-status label label-${officerStatus.color}`}>
						{officerStatus.description}
					</div>
				</div>
			</If>
		);
	};

	/**
	 * Displays modal with job activity log data
	 *
	 * _NOTE: The 'pings' page is no longer useful since the removal of background tracking from the mobile app_
	 * @param {Number} officer Officer data
	 * @param {Number} position Position data
	 * @returns {(event:Event) => void}
	 */
	onViewActivity = (officer, position) => event => {
		event.preventDefault();
		const {
			jobEvent: { TimeZoneId: jobTimeZone },
		} = this.state;
		const {
			user: { UserPreferences },
		} = this.props;

		this.setState(
			() => {
				const clockInTime = officer.ClockIn;
				const clockOutTime = officer.ClockOut;
				const clockInfo = {
					clockInTime: clockInTime
						? utils.formatTime(
								new Date(`${clockInTime}Z`),
								UserPreferences !== "useMilitaryTime",
								jobTimeZone,
							)
						: "",
					clockOutTime: clockOutTime
						? utils.formatTime(
								new Date(`${clockOutTime}Z`),
								UserPreferences !== "useMilitaryTime",
								jobTimeZone,
							)
						: "",
				};
				// If there are only 7 characters, a leading 0 is missing
				if (clockInfo.clockInTime.length === 7)
					clockInfo.clockInTime = `0${clockInfo.clockInTime}`;
				if (clockInfo.clockOutTime.length === 7)
					clockInfo.clockOutTime = `0${clockInfo.clockOutTime}`;
				return {
					currentPositionView: position,
					currentOfficerView: officer,
					currentOfficerClockIn: clockInfo.clockInTime,
					currentOfficerClockOut: clockInfo.clockOutTime,
				};
			},
			() => {
				this.toggleJobActivityLogModal();
			},
		);
	};

	/**
	 * Update the search field for a position to filter officers by name
	 * @param {Array} assignedOfficers An array of officers assigned to work a position
	 * @param {Number} positionId The ID number of the position
	 * @returns {(e:Event) => void}
	 */
	onFieldChange = (assignedOfficers, positionId) => e => {
		const { value } = e.target;
		const filteredOfficers = assignedOfficers.filter(({ OfficerProfile, OfficerName }) => {
			const officerName = OfficerProfile
				? `${OfficerProfile.FirstName} ${OfficerProfile.LastName}`
				: OfficerName;
			return officerName.toLowerCase().includes(value.toLowerCase());
		});
		this.setState(prevState => ({
			searchQuery: {
				...prevState.searchQuery,
				[positionId]: {
					...prevState.searchQuery[positionId],
					searchValue: value,
					officers: value === "" ? undefined : filteredOfficers,
				},
			},
		}));
	};

	toggleTimeCardModal = () =>
		this.setState(prevState => ({ showEditTimecardModal: !prevState.showEditTimecardModal }));

	toggleJobActivityLogModal = () =>
		this.setState(prevState => ({
			showJobActivityLogModal: !prevState.showJobActivityLogModal,
		}));

	toggleViewNoteModal = () =>
		this.setState(prevState => ({ showViewNotesModal: !prevState.showViewNotesModal }));

	toggleAddNoteModal = () =>
		this.setState(prevState => ({ showAddNoteModal: !prevState.showAddNoteModal }));

	/**
	 * Toggles the visibility of the roster view. When first made visible, if there are positions with assigned officers where
	 * officer details have not yet been retrieved, they are retrieved immediately.
	 */
	toggleRosterView = () => {
		const {
			jobEvent: { Jobs },
			assignedOfficersList,
		} = this.state;
		const posWithAssigned = Jobs.filter(pos => pos.AssignedOfficersCount > 0);
		posWithAssigned.forEach(pos => {
			if (!assignedOfficersList[pos.Id]) this.retrieveOfficersForPosition(pos.Id);
		});
		this.setState(prevState => ({ showRosterView: !prevState.showRosterView }));
	};

	toggleNotes = e => {
		const { name } = e.target;
		if (name !== "view-notes") {
			this.setState(prevState => ({ showNotes: !prevState.showNotes }));
		}
	};

	toggleJobHistoryModal = () =>
		this.setState(prevState => ({ showJobHistoryModal: !prevState.showJobHistoryModal }));

	closeUnassignmentModal = () => {
		const {
			jobEvent: { Id },
			unassignModal: { positionId },
		} = this.state;
		this.setState({ unassignModal: { ...this.initialUnassignModal } }, () => {
			this.receiveJobEvent(Id);
			this.retrieveOfficersForPosition(positionId);
		});
	};

	toggleActionDropdown = () => {
		const actions = document.getElementById("action-menu");
		if (actions.classList.contains("open")) actions.classList.remove("open");
		else actions.classList.add("open");
	};

	/**
	 * Shows additional requirements and instructions for a position
	 * @param {Number} positionId The ID number of the position
	 */
	togglePositionDetails = positionId =>
		this.setState(prevState => ({
			positionManager: {
				...prevState.positionManager,
				[positionId]: {
					...prevState.positionManager[positionId],
					showDetails: !prevState.positionManager[positionId].showDetails,
				},
			},
		}));

	toggleAssignedOfficers = (positionId, value) =>
		this.setState(prevState => ({
			positionManager: {
				...prevState.positionManager,
				[positionId]: {
					...prevState.positionManager[positionId],
					showAssigned: value,
					showSeeking: false,
				},
			},
		}));

	toggleSeekingOfficers = (positionId, value) =>
		this.setState(prevState => ({
			positionManager: {
				...prevState.positionManager,
				[positionId]: {
					...prevState.positionManager[positionId],
					showSeeking: value,
					showAssigned: false,
				},
			},
		}));

	/**
	 * Retrieves the officers who have applied for and are currently assigned to a position and updates the state with their details
	 * @param {Number} positionId The ID number of the position to retrieve officers for
	 */
	retrieveOfficersForPosition = positionId => {
		const { lang } = global;
		const assignedRequest = JobService.getAssignedOfficersByPositionId(positionId)
			.then(({ data: { AssignedOfficers, ExternalOfficers } }) => ({
				AssignedOfficers,
				ExternalOfficers: ExternalOfficers.map(o => ({ ...o, isExternal: true })),
			}))
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading Assigned Officers"),
					title: "Error",
				});
				return { AssignedOfficers: [], ExternalOfficers: [] };
			});
		const applicantRequest = JobService.getApplicantsByPositionId(positionId)
			.then(({ data }) => data)
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading Applicants"),
					title: "Error",
				});
				return [];
			});
		Promise.all([assignedRequest, applicantRequest]).then(
			([{ AssignedOfficers, ExternalOfficers }, applicants]) => {
				this.setState(prevState => ({
					assignedOfficersList: {
						...prevState.assignedOfficersList,
						[positionId]: [...AssignedOfficers, ...ExternalOfficers],
					},
					applicantList: {
						...prevState.applicantList,
						[positionId]: applicants,
					},
					positionManager: {
						...prevState.positionManager,
						[positionId]: {
							...prevState.positionManager[positionId],
							officersRetrieved: true,
						},
					},
				}));
			},
		);
	};

	/**
	 * Expands/collapses the sections that shows officers who are assigned or have applied to a position
	 * Applicant list and assigned officer list are requested and populated the first time a position is expanded to show the officer view
	 * @param {Number} positionId The ID number of the position
	 */
	toggleOfficerView = positionId => {
		const { positionManager } = this.state;
		if (!positionManager[positionId].officersRetrieved) {
			this.retrieveOfficersForPosition(positionId);
		}
		this.setState(prevState => ({
			positionManager: {
				...prevState.positionManager,
				[positionId]: {
					...prevState.positionManager[positionId],
					expanded: !prevState.positionManager[positionId].expanded,
				},
			},
		}));
	};

	/** @deprecated unused method */
	toggleAssignOfficers = Id => {
		this.setState(prevState => ({
			showAssignedOfficers: {
				...prevState.showAssignedOfficers,
				[Id]: !prevState.showAssignedOfficers[Id],
			},
		}));
	};

	/** @deprecated unused method */
	updatePositionAssignments = (positionId, assignments) => {
		const { AssignedOfficers, ExternalOfficers } = assignments;
		this.setState(prevState => ({
			jobEvent: {
				...prevState.jobEvent,
				Jobs: prevState.Jobs.map(job => {
					if (job.Id === positionId)
						return { ...job, AssignedOfficers, ExternalOfficers };
					return job;
				}),
			},
		}));
	};

	/**
	 * Indicates whether or not a position has extra details that can be shown by calling {@link togglePositionDetails}
	 * @param {Number} positionId The ID number of the position
	 * @returns {Boolean} `true` if position has further viewable details
	 */
	positionHasRequirements = positionId => {
		const {
			jobEvent: { Jobs },
		} = this.state;
		const position = Jobs.find(job => job.Id === positionId);
		if (position) {
			const {
				Details,
				RequiredJurisdictionAssets = [],
				VehicleRequired,
				VehicleTypes = [],
				DressTypeName,
				RovingPatrol,
				JobCertifications = [],
				OfficerRequirements,
				RequestedOfficerRank,
			} = position;

			return (
				Details ||
				RequiredJurisdictionAssets.length > 0 ||
				(VehicleRequired && VehicleTypes.length > 0) ||
				DressTypeName ||
				RovingPatrol ||
				JobCertifications.length > 0 ||
				OfficerRequirements ||
				RequestedOfficerRank
			);
		}
		return false;
	};

	/**
	 * Determines which position is the supervisor for the position group that a position belongs to
	 * @param {Number} positionGroupId The ID number for the position group
	 * @returns {String} A string indicating the supervisor for this position
	 */
	findSupervisorPosition = positionGroupId => {
		const { lang } = global;
		const {
			jobEvent: { Jobs },
		} = this.state;
		const supervisor = Jobs.find(
			job => job.PositionGroupId === positionGroupId && job.SupervisorPosition,
		);
		if (supervisor) return lang.t("dynamic.Reports to", { title: supervisor.Title });
		return lang.t("Reports to another position");
	};

	openJobChat = () => {
		const { initializeChat, user } = this.props;
		const { jobEvent } = this.state;
		// The initialize chat method will only create a new instance if one does not exist within the current session
		initializeChat(user, pubnub => {
			AnalyticsService.trackEvent({
				category: ANALYTICS_EVENTS_CATEGORIES.JOB_CHAT,
				action: ANALYTICS_EVENTS_ACTIONS.JOB_CHAT_OPENED,
				customParameters: {
					jobEventId: jobEvent.Id,
				},
			});
			// Trigger a subscription action to either become a member of this chat or open the chat window to this job automatically
			pubnub.subscribe({ channels: [`job-${jobEvent.Id}`] });
			pubnub.objects.setChannelMetadata({
				channel: `job-${jobEvent.Id}`,
				data: { name: jobEvent.Name },
			});
		});
	};

	/**
	 * Toggles the editing interface for an officer's timecard for working a job
	 * @param {Number} positionId A unique identifier to indicate which position is being edited (in case one officer is assigned to multiple positions on a job)
	 * @param {Object} officer A data object representing the officer whose timecard is being edited
	 */
	toggleEditTimecard = (positionId, officer) => {
		// Open Edit modal
		const {
			jobEvent,
			jobEvent: { TimeZoneId: jobTimeZone },
			associatedNonRKPayInvoices,
		} = this.state;
		const {
			user: { UserPreferences, IsViewOnly },
			flags: { timecardAttachmentsInvoiceV4 },
		} = this.props;
		this.setState(
			prevState => {
				const { timecardsToEdit } = prevState;
				const clockInTime = officer.TimeCard
					? officer.TimeCard.ClockInTime
					: officer.ClockIn;
				const clockOutTime = officer.TimeCard
					? officer.TimeCard.ClockOutTime
					: officer.ClockOut;
				const note = officer.TimeCard?.Reason;
				const timeCardInfo = {
					officerId: officer.UserId,
					posId: positionId,
					clockInTime: clockInTime
						? utils.formatTime(
								new Date(`${clockInTime}Z`),
								UserPreferences !== "useMilitaryTime",
								jobTimeZone,
							)
						: "",
					clockOutTime: clockOutTime
						? utils.formatTime(
								new Date(`${clockOutTime}Z`),
								UserPreferences !== "useMilitaryTime",
								jobTimeZone,
							)
						: "",
				};
				// If there are only 7 characters, a leading 0 is missing
				if (timeCardInfo.clockInTime.length === 7)
					timeCardInfo.clockInTime = `0${timeCardInfo.clockInTime}`;
				if (timeCardInfo.clockOutTime.length === 7)
					timeCardInfo.clockOutTime = `0${timeCardInfo.clockOutTime}`;
				timecardsToEdit.push(timeCardInfo);

				const notAllowedStatuses = [
					NON_RK_PAY_INVOICE_STATUS_V4.PENDING_CLIENT_REVIEW,
					NON_RK_PAY_INVOICE_STATUS_V4.PENDING_CLIENT_PAYMENT,
					NON_RK_PAY_INVOICE_STATUS_V4.PAID,
				];

				let allowEditTimeCard = true;

				if (!officer.PortalUserCanEditTimeCard || IsViewOnly) {
					allowEditTimeCard = false;
				}

				if (
					allowEditTimeCard &&
					timecardAttachmentsInvoiceV4 &&
					associatedNonRKPayInvoices.length > 0
				) {
					// Since it has to be an agency invoice we should only have ONE invoice and we can safely fetch first from associatedNonRKPayInvoices
					const invoice = associatedNonRKPayInvoices[0];

					const invoiceHasNotBeenSent =
						invoice?.SentDateTimeUTC === null || invoice?.SentDateTimeUTC === undefined;

					allowEditTimeCard =
						jobEvent.JobInvoicedTypeId !== JOB_INVOICED_TYPES.OFFICER_INVOICING &&
						invoiceHasNotBeenSent &&
						!notAllowedStatuses.includes(invoice?.StatusId ?? -1);
				}

				return {
					timecardsToEdit,
					currentPositionEditId: positionId,
					currentOfficerEdit: officer,
					clockinTimecardModal: timeCardInfo.clockInTime,
					clockoutTimecardModal: timeCardInfo.clockOutTime,
					notesTimecardModal: note,
					allowEditTimeCard,
				};
			},
			() => {
				this.toggleTimeCardModal();
			},
		);
	};

	/**
	 * Updates the input indicating the clock-in/clock-out time on an officer's timecard
	 * @param {Number} positionId The ID number for the position
	 * @param {Number} id The ID number for the officer
	 * @param {'clockInTime'|'clockOutTime'} property The property name to update
	 * @returns {(event: Event) => void}
	 */
	updateTime = (positionId, id, property) => event => {
		const {
			target: { value },
		} = event;
		this.setState(prevState => {
			const { timecardsToEdit } = prevState;
			const idx = timecardsToEdit.findIndex(
				({ officerId, posId }) => officerId === id && posId === positionId,
			);
			if (idx !== -1) {
				timecardsToEdit[idx] = { ...timecardsToEdit[idx], [property]: value };
				return { timecardsToEdit };
			}
			return prevState;
		});
	};

	/**
	 * Makes an API request to update the values written to an officer's timecard for working a job
	 * @param {Number} positionId A unique identifier to indicate which position is being edited (in case one officer is assigned to multiple positions on a job)
	 * @param {Object} officer A data object representing the officer whose timecard is being updated
	 */
	updateTimecard = (positionId, officer, clockinModal, clockoutModal, notesModal) => {
		const {
			assignedOfficersList,
			jobEvent: { Jobs, TimeZoneId: jobTimeZone },
		} = this.state;
		const assignment = assignedOfficersList[positionId].find(
			({ UserId }) => UserId === officer.UserId,
		);
		const position = Jobs.find(pos => pos.Id === positionId);
		const startDate = (
			officer.TimeCard ? officer.TimeCard.ClockInTime : position.StartDateTime
		).split("T")[0];
		const [ClockInTime, ClockOutTime] = utils.combineDateAndTimecardTimes(
			startDate,
			jobTimeZone,
			[clockinModal, clockoutModal],
		);
		const timecard = {
			ClockInTime,
			ClockOutTime,
			JobAssignmentId: assignment.Id,
			JobId: positionId,
			AssignedOfficerId: officer.UserId,
			Reason: notesModal,
		};
		if (officer.TimeCard) timecard.Id = officer.TimeCard.Id;

		const servicePromise = officer.TimeCard
			? TimecardService.updateTimecard(timecard)
			: TimecardService.createTimecard(timecard);
		this.toggleTimeCardModal();
		servicePromise
			.then(() => {
				JobService.getAssignedOfficersByPositionId(positionId)
					.then(({ data: { AssignedOfficers, ExternalOfficers } }) => {
						const externalOfficers = ExternalOfficers.map(item => ({
							...item,
							isExternal: true,
						}));
						const officerList = [...AssignedOfficers, ...externalOfficers];
						this.setState(prevState => ({
							assignedOfficersList: {
								...prevState.assignedOfficersList,
								[positionId]: officerList,
							},
							timecardsToEdit: prevState.timecardsToEdit.filter(
								({ officerId }) => officerId !== officer.UserId,
							),
						}));
					})
					.catch(() =>
						this.setState(prevState => ({
							timecardsToEdit: prevState.timecardsToEdit.filter(
								({ officerId }) => officerId !== officer.UserId,
							),
						})),
					);
			})
			.catch(() =>
				utils.notify({
					type: "error",
					message: "Error updating timecard information",
					timeOut: 1500,
				}),
			);
	};

	/**
	 * Calculates and returns the duration an officer worked on a job based on the clock-in/clock-out values of their timecard
	 * @param {Object} TimeCard A data object representing an officer's timecard
	 * @returns {String} a number fixed to 2 decimal places
	 */
	calculateTimeCardDuration = TimeCard => {
		if (!TimeCard) return null;
		const { ClockInTime, ClockOutTime } = TimeCard;
		const msDiff =
			new Date(`${ClockOutTime}Z`).setSeconds(0, 0) -
			new Date(`${ClockInTime}Z`).setSeconds(0, 0);
		return (msDiff / 3600000).toFixed(2);
	};

	hasPTAEOCode = positions => {
		if (positions && positions.find(pos => pos.PTAEOCodeId != null && pos.PTAEOCodeId !== ""))
			return true;
		return false;
	};

	getPayCodes = () => {
		PayCodesService.getPaycodes()
			.then(({ data }) => {
				const _agencyPayCodes = utils.parsePaycodesToReactSelect(data);
				this.setState({ agencyPayCodes: _agencyPayCodes });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: "Error loading Pay Codes",
					timeOut: 1500,
				});
			});
	};

	openAddToEstimateModal = () => {
		this.setState({ showAddToEstimateModal: true });
	}

	openAddToInvoiceModal = () => {
		this.setState({ showAddToInvoiceModal: true });
	}

	createEstimateForJob = () => {
		const { jobEvent } = this.state;
		const { router } = this.props;

		this.setState(
			{
				showAddToEstimateModal: false,
			},
			() => router.push({
				pathname: "/payments/manualinvoice",
				query: { estimate: true, clientId: jobEvent.ClientId, jobs: jobEvent.Id },
			})
		)
	}

	createInvoiceForJob = () => {
		const { jobEvent } = this.state;
		const { router } = this.props;

		this.setState(
			{
				showAddToInvoiceModal: false,
			},
			() => router.push({
				pathname: "/payments/manualinvoice",
				query: { estimate: false, clientId: jobEvent.ClientId, jobs: jobEvent.Id },
			})
		)
	}

	handleAddToEstimate = (queryParams) => {
		const { router } = this.props;

		this.setState(
			{
				showAddToEstimateModal: false,
			},
			() => {
				router.push({ pathname: "/payments/manualinvoice", query: queryParams });
			}
		);
	}

	renderAddJobToEstimateButton = (className, showIcon = false, wrapInLi = false) => {
		const { lang } = global;
		const { jobEvent } = this.state;
		const { user } = this.props;
		const isCompletedJob = jobEvent.StatusId === JOB_TYPES.completed.type;
		const officerInvoicingV3EnabledForOrg = user.Features.some(f =>
			f.toLowerCase().includes("officerinvoicingv3"),
		);

		const canRender =
			jobEvent.Id ||
			!jobEvent.IsRKPay ||
			(!isCompletedJob && officerInvoicingV3EnabledForOrg);

		if (!canRender) return null;

		const label = lang.t(isCompletedJob ? "Add to Invoice" : "Add to Estimate");

		const button = (
			<button
				type="button"
				id="add-job-estimate-button"
				className={className}
				data-prevent-navigation
				onClick={isCompletedJob ? this.openAddToInvoiceModal : this.openAddToEstimateModal}
			>
				{showIcon && <i className="glyphicon glyphicon-plus v-reflect" />}
				{label}
			</button>
		);

		return wrapInLi ? <li>{button}</li> : button;
	};

	render() {
		const { lang, history } = global;
		const {
			user,
			user: { ContactId, TimeZoneId: userTimeZone },
			router,
		} = this.props;
		const {
			showRosterView,
			showNotes,
			isLoading,
			notesLoading,
			jobEvent: { RequiredJurisdictions, ExcludedJurisdictions, ...jobEvent },
			positionManager,
			jobEventNotes,
			actionPermissions: {
				allowAddNotes,
				allowAssign,
				allowDelete,
				allowDeleteAbandonedJob,
				allowDuplicate,
				allowEdit,
				allowPost,
				allowRemovePost,
				allowRestore,
				allowUnassign,
				allowViewHistory,
			},
			searchQuery,
			applicantList,
			assignedOfficersList,
			manualAssignModal,
			unassignModal: { positionId: unassignPositionId, ...unassignModal },
			seriesConfirmationModal,
			showAddNoteModal,
			showJobHistoryModal,
			showViewNotesModal,
			showEditTimecardModal,
			showJobActivityLogModal,
			clockinTimecardModal,
			clockoutTimecardModal,
			currentOfficerClockIn,
			currentOfficerClockOut,
			currentPositionEditId,
			currentOfficerEdit,
			currentPositionView,
			currentOfficerView,
			notesTimecardModal,
			postJobModal,
			deleteJobConfirmationModal,
			agencyPayCodes,
			allowEditTimeCard,
			additionalInformation,
			associatedNonRKPayInvoices,
			associatedNonRKPayInvoiceEstimate,
			showAddToEstimateModal,
			showAddToInvoiceModal,
			showConnectedInvoicesModal,
		} = this.state;
		const { seekingReplacement } = OFFICER_STATUSES;

		const userIsJobSupervisor =
			jobEvent.SupervisorContactId === ContactId ||
			(jobEvent.SecondarySupervisorModel || []).some(
				s => s.SecondaryAgencySupervisorId === ContactId,
			);
		const allowChat = !user.isSuperAdmin() && userIsJobSupervisor;
		const isInvoicingV3 = user.Features.some(f => f.toLowerCase() === "invoicingv3");
		const isActiveJob = jobEvent.StatusId === JOB_TYPES.active.type;
		const isUpcomingJob = jobEvent.StatusId === JOB_TYPES.upcoming.type;
		const isCompletedJob = jobEvent.StatusId === JOB_TYPES.completed.type;
		const jobTimeZoneId = jobEvent.TimeZoneId || userTimeZone;
		const isActiveOrCompletedJob = isActiveJob || isCompletedJob;

		const positionDates =
			[
				...new Set(jobEvent.Jobs?.map(({ _positionStartDate }) => _positionStartDate)),
			].sort() || [];

		const hasAssignedOfficers = jobEvent.Jobs?.some(job => job.AssignedOfficers?.length > 0);

		const showAddJobToEstimateButton =
			jobEvent.Id &&
			!isActiveJob &&
			this.relocateJobFormAddToInvoicingV3Button &&
			jobEvent.JobInvoicedTypeId !== JOB_INVOICED_TYPES.SCHEDULE_ONLY &&
			!associatedNonRKPayInvoiceEstimate &&
			associatedNonRKPayInvoices.length === 0 &&
			(
				// For completed jobs (add to invoice), must be agency invoicing type and have assigned officers
				!isCompletedJob || (jobEvent.JobInvoicedTypeId === JOB_INVOICED_TYPES.AGENCY_INVOICING && hasAssignedOfficers)
			);

		return (
			<Choose>
				<When condition={showRosterView}>
					<div
						className="container padded"
						id="roster-table"
					>
						<div className="row v-align">
							<h2 className="col-xs-6 col-sm-6">{`${jobEvent.Name} #${jobEvent.Id}`}</h2>
							<h3 className="col-xs-6 col-sm-6 text-right">
								{positionDates[0]}
								{positionDates.length > 1 ? ` - ${positionDates.slice(-1)}` : ""}
							</h3>
						</div>
						<div className="roster-table-container">
							<table className="table table-bordered">
								<thead>
									<tr>
										<th>{lang.t("Position")}</th>
										<th className="text-right">{lang.t("Date")}</th>
										<th>{lang.t("Time")}</th>
										<th>{lang.t("PID")}</th>
										<th>{lang.t("Name")}</th>
										<th>{lang.t("Rank")}</th>
										<th>{lang.t("Vehicle")}</th>
										<th>{lang.t("Phone")}</th>
									</tr>
								</thead>
								<tbody>
									<For
										each="date"
										of={positionDates}
									>
										<For
											each="job"
											index="index"
											of={jobEvent.Jobs.filter(
												j => j._positionStartDate === date,
											)?.sort((a, b) => a.SortOrder - b.SortOrder)}
										>
											<For
												each="officer"
												of={(assignedOfficersList[job.Id] || []).concat(
													new Array(
														job.OfficersNeeded -
															job.AssignedOfficersCount,
													).fill(0),
												)}
												index="idx"
											>
												<Choose>
													<When condition={officer === 0}>
														<tr key={`empty_officer_${idx}`}>
															<td>
																{job.Title ?? ""}
																{job.SupervisorPosition ? "*" : ""}
															</td>
															<td className="text-right">
																{utils.formatDate(
																	new Date(
																		`${job.StartDateTime}Z`,
																	),
																	"en-US",
																	jobTimeZoneId,
																	"2-digit",
																)}
															</td>
															<td>
																{utils.formatTime(
																	new Date(
																		`${job.StartDateTime}Z`,
																	),
																	user.UserPreferences !==
																		"useMilitaryTime",
																	userTimeZone,
																)}{" "}
																-&nbsp;
																{utils.formatTime(
																	new Date(`${job.EndDateTime}Z`),
																	user.UserPreferences !==
																		"useMilitaryTime",
																	userTimeZone,
																)}
															</td>
															<td>{}</td>
															<td>{}</td>
															<td>{}</td>
															<td>
																{job.VehicleRequired ? "Yes" : "No"}
															</td>
															<td>{}</td>
														</tr>
													</When>
													<Otherwise>
														<tr key={`officer_${officer.Id}`}>
															<td>
																{job.Title ?? ""}
																{job.SupervisorPosition ? "*" : ""}
															</td>
															<td className="text-right">
																{utils.formatDate(
																	new Date(
																		`${job.StartDateTime}Z`,
																	),
																	"en-US",
																	jobTimeZoneId,
																	"2-digit",
																)}
															</td>
															<td>
																{utils.formatTime(
																	new Date(
																		`${job.StartDateTime}Z`,
																	),
																	user.UserPreferences !==
																		"useMilitaryTime",
																	userTimeZone,
																)}{" "}
																-&nbsp;
																{utils.formatTime(
																	new Date(`${job.EndDateTime}Z`),
																	user.UserPreferences !==
																		"useMilitaryTime",
																	userTimeZone,
																)}
															</td>
															<td>
																{officer.OfficerProfile?.PONumber ??
																	""}
															</td>
															<td className="text-capitalize">
																{officer.isExternal
																	? (officer.OfficerName ?? "")
																	: `${officer.OfficerProfile.FirstName} ${officer.OfficerProfile.LastName}`}
															</td>
															<td>
																{officer.OfficerProfile
																	?.OfficerRank ?? ""}
															</td>
															<td>
																{job.VehicleRequired ? "Yes" : "No"}
															</td>
															<td>
																{utils.formatPhoneNumber(
																	officer.OfficerProfile
																		?.CellPhone,
																) ?? ""}
															</td>
														</tr>
													</Otherwise>
												</Choose>
											</For>
										</For>
									</For>
								</tbody>
							</table>
						</div>
						<div className="text-right">
							<strong>
								* = {lang.t("Supervisor")} {lang.t("Position")}
							</strong>
						</div>
						<div className="roster-table-actions hidden-print">
							<div className="text-center">
								<button
									id="button-go-back"
									className="btn btn-default"
									type="button"
									onClick={this.toggleRosterView}
								>
									{lang.t("Back")}
								</button>
								<button
									id="button-print-table"
									className="btn btn-primary"
									type="button"
									onClick={() => window.print()}
								>
									{lang.t("Print")}
								</button>
							</div>
						</div>
					</div>
				</When>
				<Otherwise>
					<div
						id="job-details"
						className={`${showViewNotesModal ? "hidden-print" : ""}`}
					>
						<div className="container-fluid">
							<div className="row subnav-buttons">
								<div className="col-xs-12 col-md-2">
									<button
										type="button"
										id="go-back"
										data-test-id="jobs__go-back"
										className="btn btn-link"
										onClick={() => {
											if (history.length === 2) {
												router.push("/jobs");
											} else {
												router.goBack();
											}
										}}
									>
										<i className="rkf rkf-chevron-left" />
										&nbsp;
										{history.length === 2
											? lang.t("Jobs List")
											: lang.t("Back")}
									</button>
								</div>
								<div className="hidden-xs hidden-sm col-md-10 text-right">
									<If condition={showAddJobToEstimateButton}>
										{this.renderAddJobToEstimateButton(
											"btn btn-primary form-progress-button",
										)}
									</If>
									<If condition={allowAssign}>
										<button
											type="button"
											id="assign-officers"
											data-test-id="jobs__assign-officers"
											className="btn btn-primary"
											onClick={() => this.openManualAssignModal()}
										>
											{`${lang.t("Assign")} ${lang.t("Officer")}`}
										</button>
									</If>
									<If
										condition={
											(allowPost || allowRemovePost) && !user.isSuperAdmin()
										}
									>
										<button
											type="button"
											id={allowRemovePost ? "unpost-job" : "Post Job"}
											data-test-id={
												allowRemovePost
													? "jobs__unpost-job"
													: "jobs__post-job"
											}
											className={`btn ${allowRemovePost ? "btn-danger" : "btn-primary"}`}
											onClick={this.onPostJob}
										>
											{allowRemovePost ? lang.t("Unpost") : lang.t("Post")}
										</button>
									</If>
									<If condition={allowRestore}>
										<button
											type="button"
											id="restore-job"
											data-test-id="jobs__restore-job"
											className="btn btn-success"
											onClick={this.onRestoreJob}
										>
											{lang.t("Restore")}
										</button>
									</If>
									<If condition={allowAddNotes && !user.isSuperAdmin()}>
										<button
											type="button"
											id="add-note"
											aria-label="add job note"
											className="btn text-muted transparent-button"
											onClick={() => this.toggleAddNoteModal()}
										>
											<i className="glyphicon glyphicon-list-alt" />
											&nbsp;
											{lang.t("Add Note")}
										</button>
									</If>
									<If condition={allowEdit}>
										<Link
											id="edit-job"
											data-test-id="jobs__edit-job"
											aria-label="edit current job"
											className="btn text-muted transparent-button"
											to={`/jobs/${jobEvent.Id}/edit`}
										>
											<i className="glyphicon glyphicon-pencil" />
											&nbsp;
											{lang.t("Edit")}
										</Link>
									</If>
									<If condition={allowDuplicate}>
										<Link
											id="duplicate-job"
											data-test-id="jobs__duplicate-job"
											aria-label="duplicate current job"
											className="btn text-muted transparent-button"
											to={{
												pathname: "/jobs/add",
												query: { jobId: jobEvent.Id },
											}}
										>
											<i className="glyphicon glyphicon-duplicate" />
											&nbsp;
											{lang.t("Duplicate")}
										</Link>
									</If>
									<If condition={allowViewHistory}>
										<button
											type="button"
											id="job-history"
											data-test-id="jobs__job-history"
											aria-label="view job history"
											className="btn text-muted transparent-button"
											onClick={() => this.toggleJobHistoryModal()}
										>
											<i className="glyphicon glyphicon-repeat v-reflect" />
											&nbsp;
											{lang.t("History")}
										</button>
									</If>
									<If condition={allowDelete || allowDeleteAbandonedJob}>
										<button
											type="button"
											id="delete-job"
											data-test-id="jobs__delete-job"
											aria-label="delete job"
											className="btn text-muted transparent-button"
											onClick={this.onDeleteJob}
										>
											<i className="glyphicon glyphicon-trash" />
											&nbsp;
											{lang.t("Delete")}
										</button>
									</If>
								</div>
							</div>
						</div>
						<div className="container">
							<div className="row">
								<div className="col-xs-8 col-sm-9">
									{/* h1 on mobile devices could potentially overflow with the addition of the actions button */}
									<h1>{`${lang.t("Job")} #${jobEvent.Id || ""}`}</h1>
								</div>
								<div
									id="action-menu"
									className="col-xs-4 col-sm-3"
								>
									<If
										condition={
											associatedNonRKPayInvoiceEstimate &&
											associatedNonRKPayInvoiceEstimate.NonRKPayInvoiceId
										}
									>
										<div className="truncate text-right">
											<Link
												to={`/payments/manualinvoice?id=${associatedNonRKPayInvoiceEstimate.NonRKPayInvoiceId}&estimate=true`}
											>
												{lang.t("Estimate")} #
												{associatedNonRKPayInvoiceEstimate.InvoiceNumber}
											</Link>
										</div>
									</If>
									<If condition={associatedNonRKPayInvoices.length === 1}>
										<div
											className={classNames("text-right", {
												truncate: !associatedNonRKPayInvoiceEstimate,
											})}
										>
											<Link
												to={`/payments/manualinvoice?id=${associatedNonRKPayInvoices[0].NonRKPayInvoiceId}`}
											>
												{lang.t("Invoice")} #
												{associatedNonRKPayInvoices[0].InvoiceNumber}
											</Link>
										</div>
									</If>
									<If condition={associatedNonRKPayInvoices.length > 1}>
										<div
											className={classNames("text-right", {
												truncate: !associatedNonRKPayInvoiceEstimate,
											})}
										>
											<button
												className="btn btn-link color-primary cursor-pointer"
												style={{ paddingRight: 0 }}
												type="button"
												aria-label={lang.t("View All Invoices")}
												onClick={() => this.setState({ showConnectedInvoicesModal: true })}
											>
												{lang.t("Invoices")}: {lang.t("View All")}
											</button>
										</div>
									</If>
									<button
										type="button"
										className="btn btn-primary btn-block dropdown-toggle hidden-md hidden-lg"
										data-test-id="jobs__actions-dropdown"
										onClick={() => this.toggleActionDropdown()}
										aria-haspopup="true"
										aria-expanded="false"
									>
										{lang.t("Actions")}
										&nbsp;
										<span className="caret" />
									</button>
									<ul className="dropdown-menu">
										<If condition={showAddJobToEstimateButton}>
											{this.renderAddJobToEstimateButton(
												"btn btn-block text-left",
												true,
												true,
											)}
										</If>
										<If condition={allowAssign}>
											<li>
												<button
													type="button"
													id="assign-officers-dropdown"
													data-test-id="jobs__assign-officers-dropdown"
													className="btn btn-block text-left"
													onClick={() => this.openManualAssignModal()}
												>
													<i className="glyphicon glyphicon-user" />
													{lang.t("Assign")}
												</button>
											</li>
										</If>
										<If
											condition={
												(allowPost || allowRemovePost) &&
												!user.isSuperAdmin()
											}
										>
											<li>
												<button
													type="button"
													id={
														allowRemovePost
															? "unpost-job-dropdown"
															: "post-job-dropdown"
													}
													data-test-id={
														allowRemovePost
															? "jobs__unpost-job"
															: "jobs__post-job"
													}
													className="btn btn-block text-left"
													onClick={this.onPostJob}
												>
													<i
														className={classNames(
															"glyphicon glyphicon-share-alt",
															{
																"v-reflect": allowRemovePost,
															},
														)}
													/>
													{allowRemovePost
														? lang.t("Unpost")
														: lang.t("Post")}
												</button>
											</li>
										</If>
										<If condition={allowRestore}>
											<li>
												<button
													type="button"
													id="restore-job-dropdown"
													data-test-id="jobs__restore-job-dropdown"
													className="btn btn-block text-left"
													onClick={this.onRestoreJob}
												>
													<i className="glyphicon glyphicon-share-alt v-reflect" />
													{lang.t("Restore")}
												</button>
											</li>
										</If>
										<If condition={allowAddNotes && !user.isSuperAdmin()}>
											<li>
												<button
													type="button"
													id="add-note-dropdown"
													data-test-id="jobs__add-note-dropdown"
													aria-label="add job note"
													className="btn btn-block text-left"
													onClick={() => this.toggleAddNoteModal()}
												>
													<i className="glyphicon glyphicon-list-alt" />
													{lang.t("Add Note")}
												</button>
											</li>
										</If>
										<If condition={allowEdit}>
											<li>
												<Link
													id="edit-job-dropdown"
													data-test-id="jobs__edit-job-dropdown"
													aria-label="edit current job"
													className="btn btn-block text-left"
													to={`/jobs/${jobEvent.Id}/edit`}
												>
													<i className="glyphicon glyphicon-pencil" />
													{lang.t("Edit")}
												</Link>
											</li>
										</If>
										<If condition={allowDuplicate}>
											<li>
												<Link
													id="duplicate-job-dropdown"
													data-test-id="jobs__duplicate-job-dropdown"
													aria-label="duplicate current job"
													className="btn btn-block text-left"
													to={{
														pathname: "/jobs/add",
														query: { jobId: jobEvent.Id },
													}}
												>
													<i className="glyphicon glyphicon-duplicate" />
													{lang.t("Duplicate")}
												</Link>
											</li>
										</If>
										<If condition={allowViewHistory}>
											<li>
												<button
													type="button"
													id="job-history-dropdown"
													data-test-id="jobs__job-history-dropdown"
													aria-label="view job history"
													className="btn btn-block text-left"
													onClick={() => this.toggleJobHistoryModal()}
												>
													<i className="glyphicon glyphicon-repeat v-reflect" />
													{lang.t("History")}
												</button>
											</li>
										</If>
										<If condition={allowDelete || allowDeleteAbandonedJob}>
											<li>
												<button
													type="button"
													id="delete-job-dropdown"
													data-test-id="jobs__delete-job-dropdown"
													aria-label="delete job"
													className="btn btn-block text-left"
													onClick={this.onDeleteJob}
												>
													<i className="glyphicon glyphicon-trash" />
													{lang.t("Delete")}
												</button>
											</li>
										</If>
									</ul>
								</div>
							</div>
							{/* Job Details Panel */}
							<div className="row">
								<div className="col-sm-12">
									<div className="details-panel">
										<div className="row">
											<div className="col-sm-6 overflow-job-name">
												<h4>{jobEvent.Name}</h4>
												<div className="text-muted">{`${jobEvent.ClientName}`}</div>
											</div>
											<div className="col-sm-6">
												<div className="row">
													<div className="col-xs-4 text-center">
														<h4>{jobEvent.ApplicantsNeeded}</h4>
														<p className="text-muted">
															{lang.t("Needed")}
														</p>
													</div>
													<div className="col-xs-4 text-center">
														<h4>{jobEvent.ApplicantsApplied}</h4>
														<p className="text-muted">
															{lang.t("Applicants")}
														</p>
													</div>
													<div className="col-xs-4 text-center">
														<h4>{jobEvent.ApplicantsAssigned}</h4>
														<p className="text-muted">
															{lang.t("Assigned")}
														</p>
													</div>
												</div>
											</div>
										</div>
										<div className="separator separator-solid" />
										<div className="row">
											<div className="col-sm-3">
												<div className="row">
													<div className="col-xs-2 text-muted detail-icon">
														<i className="rkf rkf-calendar" />
													</div>
													<div className="col-xs-10">
														<strong>{lang.t("Event Date")}</strong>
														<p>{this.renderEventDate()}</p>
													</div>
												</div>
											</div>
											<div className="col-sm-3">
												<div className="row">
													<div className="col-xs-2 text-muted detail-icon">
														<i className="rkf rkf-dollar" />
													</div>
													<div className="col-xs-10">
														<div>
															<Choose>
																<When
																	condition={utils.doItHavePayCodeAdded(
																		jobEvent.Jobs,
																	)}
																>
																	<strong>
																		{lang.t("Pay Code")}
																	</strong>
																</When>
																<Otherwise>
																	<strong>
																		{lang.t("Officer Rate")}
																	</strong>
																	<p>
																		{this.renderOfficerRate()}
																	</p>
																</Otherwise>
															</Choose>
														</div>
														<div>
															<If
																condition={this.hasPTAEOCode(
																	jobEvent.Jobs,
																)}
															>
																<strong>
																	{lang.t("PTAEO Code")}
																</strong>
															</If>
														</div>
													</div>
												</div>
											</div>
											<div className="col-sm-3">
												<div className="row">
													<div className="col-xs-2 text-muted detail-icon">
														<i className="rkf rkf-clock" />
													</div>
													<div className="col-xs-10">
														<strong>{lang.t("Hours")}</strong>
														<p>{`${jobEvent.ScheduledHours} ${lang.t("Hours")}`}</p>
													</div>
												</div>
											</div>
											<div className="col-sm-3">
												<div className="row">
													<div className="col-xs-2 detail-icon">
														<i
															className={classNames("rkf", {
																"rkf-eye text-success":
																	jobEvent.Posted,
																"rkf-eye-hidden text-danger":
																	!jobEvent.Posted,
															})}
														/>
													</div>
													<div className="col-xs-10">
														<strong>{lang.t("Posted Status")}</strong>
														<p>{this.renderPostedStatus()}</p>
													</div>
												</div>
											</div>
										</div>
										<div className="separator separator-solid" />
										<div className="row">
											<If
												condition={
													user.isSuperAdmin() ||
													user.isLEA() ||
													user.isClient()
												}
											>
												<div className="col-sm-3">
													{`${lang.t("Agency")}: `}
													<span className="text-muted">
														{jobEvent.AgencyName}
													</span>
												</div>
											</If>
											<If condition={!user.isCoordinator()}>
												<div className="col-sm-3">
													{`${lang.t("Project")}: `}
													<span className="text-muted">
														{jobEvent.ProjectName}
													</span>
												</div>
											</If>
											<If condition={jobEvent.JobEventTypeName}>
												<div className="col-sm-6">
													{`${lang.t("Type")}: `}
													<span className="text-muted">
														{jobEvent.JobEventTypeName}
													</span>
												</div>
											</If>
											<div className="col-sm-12 job-desc">
												{lang.t("Description")}
												<div className="text-muted">
													{jobEvent.Details || ""}
												</div>
											</div>
											{this.jobDescriptionAttachments &&
												additionalInformation?.Attachments &&
												additionalInformation.Attachments.length > 0 && (
													<div className="col-sm-12 job-desc">
														{lang.t("Attachments")}
														<ul className="list-unstyled flex flex-wrap flex-direction-row flex-gap-8">
															{additionalInformation.Attachments.map(
																attachment => (
																	<AttachmentListItem
																		key={attachment.id}
																		attachment={{
																			id: attachment.AttachmentId,
																			name: attachment.AttachmentName,
																			url: attachment.Url,
																			existingFile: true,
																		}}
																		readOnly
																	/>
																),
															)}
														</ul>
													</div>
												)}
											<If
												condition={
													jobEvent.JobEventTags &&
													jobEvent.JobEventTags.length > 0
												}
											>
												<div className="col-sm-12 job-desc">
													{lang.t("Job Tags")}
													<div className="text-muted tag-group">
														<For
															each="jobtag"
															of={jobEvent.JobEventTags || []}
														>
															<span
																key={jobtag.Id}
																className="text-muted tag-container"
															>
																{jobtag.Name}
															</span>
														</For>
													</div>
												</div>
											</If>
										</div>
										{/*
										<If condition={(jobEvent.IsRKPay || jobEvent.JobInvoicedTypeId === 1 || jobEvent.JobInvoicedTypeId === 2) && !isInvoicingV3 && !user.TransferPaymentToJurisdiction && jobEvent.TransferPaymentsToAgency == null}>
											<RKPayRibbon />
										</If>
										*/}
									</div>
								</div>
							</div>
							{/* Job Details Panel */}

							<div className="row">
								<div className="col-md-8">
									{/* Job Positions Panel */}
									<div className="positions-panel">
										<div className="row">
											<div className="col-xs-6">
												<h4>{lang.t("Positions")}</h4>
											</div>
											<div className="col-xs-6 text-right">
												<button
													id="view-roster"
													className="btn btn-link"
													type="button"
													onClick={() => this.toggleRosterView()}
												>
													{lang.t("View Roster")}
												</button>
											</div>
										</div>
										<For
											each="date"
											of={positionDates}
										>
											<If
												condition={
													jobEvent.JobEventTypeId ===
													FORM_JOB_TYPES.EVENT.value
												}
											>
												<div className="position-date">
													<strong>{date}</strong>
												</div>
											</If>
											{/** We are using the SortOrder property to make sure the
											 * order the positions are displayed in never changes */}
											<For
												each="position"
												of={jobEvent.Jobs.filter(
													j => j._positionStartDate === date,
												)?.sort((a, b) => a.SortOrder - b.SortOrder)}
												index="index"
											>
												<div
													role="button"
													name="positionElement"
													id={position.Id}
													key={`position_${position.Id}`}
													tabIndex="0"
													onClick={() =>
														this.toggleOfficerView(position.Id)
													}
													onKeyPress={({ key }) => {
														if (key === "Enter")
															this.toggleOfficerView(position.Id);
													}}
													className={classNames(
														"row position-overview clickable-row",
														{
															active: positionManager[position.Id]
																.expanded,
															"reporting-position":
																position.PositionGroupId &&
																!position.SupervisorPosition,
														},
													)}
												>
													<div
														className={classNames({
															"col-xs-6 col-sm-3":
																isActiveJob || isUpcomingJob,
															"col-xs-12 col-sm-7":
																!isActiveJob && !isUpcomingJob,
														})}
													>
														<div
															className="position-title"
															title={`${position.Title}${position.SupervisorPosition ? " (Supervisor Position)" : ""}`}
														>
															{position.Title}
														</div>
														{/* <If condition={position.SupervisorPosition}>
															<span className="label label-primary" title="Supervisor Position">{lang.t('Supervisor')}</span>
															&nbsp;
														</If>
														<If condition={position.RequestedOfficerRank}>
															<span
																className="label label-info"
																title={`${position.OfficerRankRequired ? 'Specific Rank' : 'Minimum Rank'} - ${position.RequestedOfficerRank}`}
															>
																{position.RequestedOfficerRank}
															</span>
														</If> */}
													</div>
													<div className="col-sm-2">
														<If
															condition={
																jobEvent.JobEventTypeId ===
																FORM_JOB_TYPES.EVENT.value
															}
														>
															<div>
																{utils.formatTime(
																	new Date(
																		`${position.StartDateTime}Z`,
																	),
																	user.UserPreferences !==
																		"useMilitaryTime",
																	jobTimeZoneId,
																)}
																&nbsp;-&nbsp;
																{utils.formatTime(
																	new Date(
																		`${position.EndDateTime}Z`,
																	),
																	user.UserPreferences !==
																		"useMilitaryTime",
																	jobTimeZoneId,
																)}
																<If condition={position.FlexJob}>
																	<span className="label label-info">
																		{lang.t("FLEX")}
																	</span>
																</If>
															</div>
															<If
																condition={
																	userTimeZone !== jobTimeZoneId
																}
															>
																<div className="hidden-xs">
																	<small>
																		(
																		{utils.formatTime(
																			new Date(
																				`${position.StartDateTime}Z`,
																			),
																			user.UserPreferences !==
																				"useMilitaryTime",
																			userTimeZone,
																		)}
																		&nbsp;-&nbsp;
																		{utils.formatTime(
																			new Date(
																				`${position.EndDateTime}Z`,
																			),
																			user.UserPreferences !==
																				"useMilitaryTime",
																			userTimeZone,
																		)}
																		&nbsp;
																		{lang.t("local time")})
																	</small>
																</div>
															</If>
														</If>
														<If condition={position.FlexJob}>
															<small>
																{position.FlexJobMinHours}{" "}
																{lang.t("Hr Minimum")}
															</small>
														</If>
													</div>
													<If condition={isActiveJob || isUpcomingJob}>
														<div className="col-xs-6 col-sm-2">
															<Choose>
																<When
																	condition={
																		isUpcomingJob &&
																		position.ApplicationsPendingCount
																	}
																>
																	<div className="tag tag-danger">
																		{lang.t(
																			"dynamic.New Applicant",
																			{
																				/* eslint-disable-next-line camelcase */
																				smart_count:
																					position.ApplicationsPendingCount,
																			},
																		)}
																	</div>
																</When>
																{/* Position is in progress */}
																<When
																	condition={
																		new Date(
																			`${position.StartDateTime}Z`,
																		).getTime() < Date.now() &&
																		Date.now() <
																			new Date(
																				`${position.EndDateTime}Z`,
																			).getTime()
																	}
																>
																	<div className="tag tag-success">
																		Active
																	</div>
																</When>
															</Choose>
														</div>
													</If>
													<Choose>
														<When
															condition={
																position.Rate || !position.PayCodeId
															}
														>
															<div className="col-xs-3 col-sm-2">
																<div>${position.Rate}/hr</div>
																<If
																	condition={
																		position.PTAEOCodeId !=
																			null &&
																		position.PTAEOCodeId !== ""
																	}
																>
																	<div
																		style={{
																			marginTop: "10px",
																		}}
																	>
																		{position.PTAEOCode}
																	</div>
																</If>
															</div>
														</When>
														<Otherwise>
															<div
																className="col-xs-3 col-sm-2"
																title={
																	utils.getSelectedPayCode(
																		agencyPayCodes,
																		position.PayCodeId,
																	)?.title
																}
															>
																<div>
																	{
																		utils.getSelectedPayCode(
																			agencyPayCodes,
																			position.PayCodeId,
																		)?.label
																	}
																</div>
																<If
																	condition={
																		position.PTAEOCodeId !=
																			null &&
																		position.PTAEOCodeId !== ""
																	}
																>
																	<div
																		style={{
																			marginTop: "10px",
																		}}
																	>
																		{position.PTAEOCode}
																	</div>
																</If>
															</div>
														</Otherwise>
													</Choose>
													<div className="col-xs-4 col-sm-2 text-right">
														{`${position.AssignedOfficersCount} / ${position.OfficersNeeded}`}
														<div className="text-muted">
															{lang.t("Filled")}
														</div>
													</div>
													<div className="col-xs-4 col-sm-1 text-right">
														<i
															className={classNames("rkf", {
																"rkf-chevron-down":
																	!positionManager[position.Id]
																		.expanded,
																"rkf-chevron-up":
																	positionManager[position.Id]
																		.expanded,
															})}
														/>
													</div>
													&nbsp; &nbsp;
													<If condition={position.SupervisorPosition}>
														<span
															className="label label-primary"
															title="Supervisor Position"
														>
															{lang.t("Supervisor")}
														</span>
														&nbsp;
													</If>
													<If condition={position.RequestedOfficerRank}>
														<span
															className="label label-info"
															title={`${position.OfficerRankRequired ? "Specific Rank" : "Minimum Rank"} - ${position.RequestedOfficerRank}`}
														>
															{position.RequestedOfficerRank}
														</span>
													</If>
													<If
														condition={
															position.PositionGroupId &&
															!position.SupervisorPosition
														}
													>
														<div className="col-sm-12">
															<small>
																<em>
																	{this.findSupervisorPosition(
																		position.PositionGroupId,
																	)}
																</em>
															</small>
														</div>
													</If>
												</div>
												<div
													className={`collapsing-panel row${positionManager[position.Id].expanded ? " expanded" : ""}`}
												>
													<div className="col-xs-6 col-sm-4 text-center position-tab">
														<button
															type="button"
															className={`btn btn-link${positionManager[position.Id].showAssigned ? " active" : ""}`}
															onClick={() =>
																this.toggleAssignedOfficers(
																	position.Id,
																	true,
																)
															}
														>
															{`${lang.t("Assigned Officers")} (${position.AssignedOfficersCount})`}
														</button>
													</div>
													<div className="col-xs-6 col-sm-4 text-center position-tab">
														<button
															type="button"
															className={`btn btn-link${
																!positionManager[position.Id]
																	.showAssigned &&
																!positionManager[position.Id]
																	.showSeeking
																	? " active"
																	: ""
															}`}
															onClick={() =>
																this.toggleAssignedOfficers(
																	position.Id,
																	false,
																)
															}
														>
															{`${lang.t("Applicants")} (${(applicantList[position.Id] || []).length})`}
														</button>
													</div>
													<div className="col-xs-6 col-sm-4 text-center position-tab">
														<button
															type="button"
															className={`btn btn-link${positionManager[position.Id].showSeeking ? " active" : ""}`}
															onClick={() =>
																this.toggleSeekingOfficers(
																	position.Id,
																	true,
																)
															}
														>
															{`${lang.t("Seeking Replacement")} (${
																(
																	assignedOfficersList[
																		position.Id
																	] || []
																).filter(
																	o =>
																		o.OfficerStatus ===
																		seekingReplacement.type,
																).length
															})`}
														</button>
													</div>
													<Choose>
														<When
															condition={
																positionManager[position.Id]
																	.showAssigned
															}
														>
															<If
																condition={
																	(
																		assignedOfficersList[
																			position.Id
																		] || []
																	).length > 0
																}
															>
																<div className="col-xs-12 vertical-spacing">
																	<div className="input-group">
																		<span className="input-group-addon">
																			<i className="rkf rkf-search" />
																		</span>
																		<input
																			type="search"
																			value={
																				searchQuery[
																					position.Id
																				]?.searchValue || ""
																			}
																			name="applicant-search"
																			className="form-control"
																			placeholder="Search by name"
																			onChange={e => {
																				this.onFieldChange(
																					assignedOfficersList[
																						position.Id
																					] || [],
																					position.Id,
																				)(e);
																			}}
																		/>
																	</div>
																	<If
																		condition={
																			searchQuery[position.Id]
																				?.officers
																				?.length === 0
																		}
																	>
																		<div className="text-center text-muted vertical-spacing">
																			{lang.t(
																				"No Officers Found",
																			)}
																		</div>
																	</If>
																</div>
															</If>
															<For
																index="index"
																each="officer"
																of={
																	searchQuery[position.Id]
																		?.officers ||
																	assignedOfficersList[
																		position.Id
																	] ||
																	[]
																}
															>
																<div
																	className="col-xs-12"
																	key={`assigned_officer_${index}`}
																>
																	<div className="row position-officer">
																		<div className="col-xs-4 col-sm-3">
																			<Image
																				className="profile-img img-circle"
																				src={
																					officer
																						.OfficerProfile
																						?.ContactPhotoUrl ||
																					""
																				}
																				fallbackSrc="avatar"
																			/>
																			<If
																				condition={
																					officer.RecurrenceRuleJobAssignment &&
																					officer.RecurrenceRuleJobAssignment !==
																						null
																				}
																			>
																				<Tooltip content="This officer is assigned to multiple occurrences of this job">
																					<i className="rkf rkf-refresh recurring-icon" />
																				</Tooltip>
																			</If>
																		</div>
																		<div className="col-xs-8 col-sm-6">
																			<div className="row">
																				<div className="col-sm-12">
																					<strong className="text-capitalize">
																						<Choose>
																							<When
																								condition={
																									officer.isExternal
																								}
																							>
																								{officer.OfficerName ||
																									""}
																							</When>
																							<Otherwise>
																								{officer
																									.OfficerProfile
																									?.FirstName ||
																									""}
																								&nbsp;
																								{officer
																									.OfficerProfile
																									?.LastName ||
																									""}
																							</Otherwise>
																						</Choose>
																					</strong>
																					<If
																						condition={
																							officer.CreateDateUTC
																						}
																					>
																						<br />
																						<small className="text-muted">
																							{utils.formatDate(
																								new Date(
																									`${officer.CreateDateUTC}Z`,
																								),
																								"en-US",
																								userTimeZone,
																								"2-digit",
																							)}
																							&nbsp;
																							{utils.formatTime(
																								new Date(
																									`${officer.CreateDateUTC}Z`,
																								),
																								user.UserPreferences !==
																									"useMilitaryTime",
																								userTimeZone,
																							)}
																							&nbsp;
																							{`(${utils
																								.parseUTCDateToTimeZone(
																									officer.CreateDateUTC,
																									userTimeZone,
																								)
																								.fromNow()})`}
																						</small>
																					</If>
																				</div>
																			</div>
																			<Choose>
																				<When
																					condition={
																						!officer.isExternal
																					}
																				>
																					<div className="row">
																						<div className="col-sm-12">
																							{officer
																								.OfficerProfile
																								?.OfficerRank ||
																								""}
																							{officer
																								.OfficerProfile
																								?.OfficerRank &&
																							officer
																								.OfficerProfile
																								?.JurisdictionName
																								? ", "
																								: ""}
																							{officer
																								.OfficerProfile
																								?.JurisdictionName ||
																								""}
																						</div>
																					</div>
																					<div className="row">
																						<If
																							condition={
																								officer
																									.OfficerProfile
																									?.CellPhone
																							}
																						>
																							<div className="col-sm-12">
																								<i className="rkf rkf-phone" />
																								&nbsp;
																								{utils.formatPhoneNumber(
																									officer
																										.OfficerProfile
																										.CellPhone ||
																										"",
																								)}
																							</div>
																						</If>
																						<If
																							condition={
																								officer
																									.OfficerProfile
																									?.Email
																							}
																						>
																							<div
																								className="col-sm-12 truncate"
																								title={
																									officer
																										.OfficerProfile
																										.Email
																								}
																							>
																								<i className="rkf rkf-email" />
																								&nbsp;
																								{officer
																									.OfficerProfile
																									.Email ||
																									""}
																							</div>
																						</If>
																					</div>
																					<div>
																						<If
																							condition={
																								!isActiveOrCompletedJob &&
																								officer.OfficerStatus ===
																									seekingReplacement.type
																							}
																						>
																							<div className="label label-danger">
																								{
																									seekingReplacement.description
																								}
																							</div>
																						</If>
																					</div>
																				</When>
																				<Otherwise>
																					<div>
																						<p>
																							{lang.t(
																								"Non-RollKall Officer",
																							)}
																						</p>
																					</div>
																				</Otherwise>
																			</Choose>
																		</div>
																		<If
																			condition={
																				isActiveOrCompletedJob &&
																				!officer.isExternal
																			}
																		>
																			<div className="col-sm-3 officer-right-column">
																				<div className="row">
																					<div className="col-xs-6 col-sm-12">
																						<small>
																							<Choose>
																								<When
																									condition={
																										officer
																											.TimeCard
																											?.ClockInTime
																									}
																								>
																									{utils.formatTime(
																										new Date(
																											`${officer.TimeCard.ClockInTime}Z`,
																										),
																										user.UserPreferences !==
																											"useMilitaryTime",
																										jobTimeZoneId,
																									)}
																								</When>
																								<When
																									condition={
																										officer.ClockIn
																									}
																								>
																									{utils.formatTime(
																										new Date(
																											`${officer.ClockIn}Z`,
																										),
																										user.UserPreferences !==
																											"useMilitaryTime",
																										jobTimeZoneId,
																									)}
																								</When>
																								<Otherwise>
																									--
																								</Otherwise>
																							</Choose>
																							&nbsp;
																							{lang.t(
																								"Clock In",
																							)}
																						</small>
																					</div>
																					<div className="col-xs-6 col-sm-12">
																						<small>
																							<Choose>
																								<When
																									condition={
																										officer
																											.TimeCard
																											?.ClockOutTime
																									}
																								>
																									{utils.formatTime(
																										new Date(
																											`${officer.TimeCard.ClockOutTime}Z`,
																										),
																										user.UserPreferences !==
																											"useMilitaryTime",
																										jobTimeZoneId,
																									)}
																								</When>
																								<When
																									condition={
																										officer.ClockOut
																									}
																								>
																									{utils.formatTime(
																										new Date(
																											`${officer.ClockOut}Z`,
																										),
																										user.UserPreferences !==
																											"useMilitaryTime",
																										jobTimeZoneId,
																									)}
																								</When>
																								<Otherwise>
																									--
																								</Otherwise>
																							</Choose>
																							&nbsp;
																							{lang.t(
																								"Clock Out",
																							)}
																						</small>
																					</div>
																					<div className="col-xs-6 col-sm-12">
																						<div className="label label-default margin-right-12">
																							{`${utils.abbreviateNumber(
																								this.calculateTimeCardDuration(
																									officer.TimeCard,
																								) ||
																									officer.WorkedHours ||
																									0,
																							)} Total Hours`}
																						</div>
																						<button
																							type="button"
																							className="btn btn-link padding-left-0"
																							onClick={() =>
																								this.toggleEditTimecard(
																									position.Id,
																									officer,
																								)
																							}
																						>
																							<i className="rkf rkf-Timecard" />
																							<span className="margin-left-2">
																								{lang.t(
																									"Timecard",
																								)}
																							</span>
																						</button>
																					</div>
																					<If
																						condition={
																							officer.OfficerStatus !==
																							OFFICER_STATUSES
																								.none
																								.type
																						}
																					>
																						{this.renderOfficerStatus(
																							officer.OfficerStatus,
																						)}
																					</If>
																				</div>
																			</div>
																		</If>
																		<div
																			className={classNames(
																				"col-xs-12",
																				{
																					"col-sm-6 col-sm-offset-6":
																						isActiveOrCompletedJob,
																					"col-sm-3 button-container":
																						!isActiveOrCompletedJob,
																				},
																			)}
																		>
																			<div className="row">
																				<div
																					className={`col-xs-6 ${isActiveOrCompletedJob ? "" : "col-sm-12"}`}
																				>
																					<If
																						condition={
																							allowUnassign &&
																							!officer.ClockIn
																						}
																					>
																						<button
																							type="button"
																							className="btn btn-danger btn-block"
																							onClick={this.onChangeApplicantStatus(
																								officer,
																								position.Id,
																								true,
																							)}
																						>
																							{lang.t(
																								"Unassign",
																							)}
																						</button>
																					</If>
																				</div>
																				<If
																					condition={
																						!officer.isExternal
																					}
																				>
																					<div
																						className={`col-xs-6 ${isActiveOrCompletedJob ? "" : "col-sm-12"}`}
																					>
																						<button
																							type="button"
																							className="btn btn-primary btn-block"
																							onClick={this.onViewActivity(
																								officer,
																								position,
																							)}
																						>
																							{lang.t(
																								"View Activity",
																							)}
																						</button>
																					</div>
																				</If>
																			</div>
																		</div>
																		<div className="col-xs-8 col-sm-6">
																			{officer.IsMinimumPayRate && (
																				<small>
																					*This officer
																					has a minimum
																					pay rate set by
																					their
																					department.
																				</small>
																			)}
																		</div>
																	</div>
																</div>
															</For>
															<If
																condition={
																	(
																		assignedOfficersList[
																			position.Id
																		] || []
																	).length === 0
																}
															>
																<div className="col-xs-12 no-officers text-center text-muted">
																	{lang.t("No officers assigned")}
																</div>
															</If>
															<If condition={allowAssign}>
																<div className="col-xs-12 no-officers text-center">
																	<button
																		type="button"
																		className="btn btn-primary"
																		onClick={() =>
																			this.openManualAssignModal(
																				position.Id,
																			)
																		}
																	>
																		{lang.t(
																			"Manually Assign Officers",
																		)}
																	</button>
																</div>
															</If>
														</When>
														<When
															condition={
																!positionManager[position.Id]
																	.showAssigned &&
																!positionManager[position.Id]
																	.showSeeking
															}
														>
															<For
																each="applicant"
																of={
																	applicantList[position.Id] || []
																}
															>
																<div
																	className="col-xs-12 applicant-container"
																	key={`applied_officer_${applicant.Id}`}
																>
																	<div className="row position-officer">
																		<div className="col-xs-4 col-sm-2">
																			<Image
																				className="profile-img img-circle"
																				src={
																					applicant.User
																						.ContactPhotoUrl ||
																					""
																				}
																				fallbackSrc="avatar"
																			/>
																		</div>
																		<div className="col-xs-8 col-sm-6 officer-left-column">
																			<div className="row">
																				<div className="col-sm-12">
																					<strong className="text-capitalize">
																						{`${applicant.User.FirstName || ""} ${applicant.User.LastName || ""}`}
																					</strong>
																					<If
																						condition={
																							applicant.CreateDateUTC
																						}
																					>
																						<br />
																						<small className="text-muted">
																							{utils.formatDate(
																								new Date(
																									`${applicant.CreateDateUTC}Z`,
																								),
																								"en-US",
																								userTimeZone,
																								"2-digit",
																							)}
																							&nbsp;
																							{utils.formatTime(
																								new Date(
																									`${applicant.CreateDateUTC}Z`,
																								),
																								user.UserPreferences !==
																									"useMilitaryTime",
																								userTimeZone,
																							)}
																							&nbsp;
																							{`(${utils
																								.parseUTCDateToTimeZone(
																									applicant.CreateDateUTC,
																									userTimeZone,
																								)
																								.fromNow()})`}
																						</small>
																					</If>
																				</div>
																			</div>
																			<div className="row">
																				<div className="col-sm-12">
																					{applicant.User
																						.OfficerRank ||
																						""}
																					{applicant.User
																						.OfficerRank &&
																					applicant.User
																						.JurisdictionName
																						? ", "
																						: ""}
																					{applicant.User
																						.JurisdictionName ||
																						""}
																				</div>
																			</div>
																			<div className="row">
																				<If
																					condition={
																						applicant
																							.User
																							.CellPhone
																					}
																				>
																					<div className="col-sm-12">
																						<i className="rkf rkf-phone" />
																						&nbsp;
																						{utils.formatPhoneNumber(
																							applicant
																								.User
																								.CellPhone ||
																								"",
																						)}
																					</div>
																				</If>
																				<If
																					condition={
																						applicant
																							.User
																							.Email
																					}
																				>
																					<div className="col-sm-12 truncate">
																						<i className="rkf rkf-email" />
																						&nbsp;
																						{applicant
																							.User
																							.Email ||
																							""}
																					</div>
																				</If>
																			</div>
																			<div className="row">
																				<If
																					condition={
																						!isActiveOrCompletedJob
																					}
																				>
																					<If
																						condition={
																							applicant.TotalJobsWorked
																						}
																					>
																						<div className="label label-default">
																							{`${utils.abbreviateNumber(applicant.TotalJobsWorked)} Jobs Worked`}
																						</div>
																					</If>
																					<If
																						condition={
																							applicant.UpcomingJobsScheduled
																						}
																					>
																						<div className="label label-default">
																							{utils.abbreviateNumber(
																								applicant.UpcomingJobsScheduled,
																							)}
																							&nbsp;
																							{lang.t(
																								"Upcoming Jobs",
																							)}
																						</div>
																					</If>
																				</If>
																			</div>
																		</div>
																		<div className="col-xs-12 col-sm-3 text-right button-container">
																			<Choose>
																				<When
																					condition={
																						applicant
																							.User
																							.DoNotHire
																					}
																				>
																					<div className="label label-danger">
																						{lang.t(
																							"Officer is on Do Not Hire list",
																						)}
																					</div>
																				</When>
																				<When
																					condition={
																						allowAssign &&
																						!(
																							assignedOfficersList[
																								position
																									.Id
																							] || []
																						).find(
																							obj =>
																								obj.UserId ===
																								applicant.UserId,
																						) &&
																						this.displayActionButtons(
																							applicant,
																						)
																					}
																				>
																					<div className="row">
																						<div className="col-xs-6 col-sm-12">
																							<button
																								type="button"
																								className="btn btn-block btn-danger"
																								onClick={this.onChangeApplicantStatus(
																									{
																										type: "decline",
																										userId: applicant.UserId,
																									},
																									applicant.JobId,
																								)}
																							>
																								{lang.t(
																									"Decline",
																								)}
																							</button>
																						</div>
																						<div className="col-xs-6 col-sm-12">
																							<button
																								type="button"
																								className="btn btn-block btn-success"
																								onClick={() =>
																									this.acceptApplication(
																										applicant,
																									)
																								}
																							>
																								{lang.t(
																									"Accept",
																								)}
																							</button>
																						</div>
																					</div>
																				</When>
																				<When
																					condition={
																						!allowAssign &&
																						this.isApplicationPending(
																							applicant,
																						)
																					}
																				>
																					<div className="label label-warning">
																						{lang.t(
																							"Pending Approval",
																						)}
																					</div>
																				</When>
																			</Choose>
																			<If
																				condition={
																					applicant.ApprovedDateTimeUTC
																				}
																			>
																				<span className="label label-success">
																					{applicant.FromRequest
																						? "Approved"
																						: "Manual"}
																				</span>
																			</If>
																			<If
																				condition={
																					(applicant.DenialDateTimeUTC ||
																						applicant.DeleteDateUTC) &&
																					applicant.UnassignmentReason !==
																						1
																				}
																			>
																				<span className="label label-danger">
																					{lang.t(
																						"Denied",
																					)}
																				</span>
																			</If>
																			<If
																				condition={
																					applicant.ApprovedDateTimeUTC &&
																					applicant.UnassignmentReason ===
																						1
																				}
																			>
																				<span className="label label-danger">
																					{lang.t(
																						"Called Off",
																					)}
																				</span>
																			</If>
																		</div>
																	</div>
																</div>
															</For>
															<If
																condition={
																	(
																		applicantList[
																			position.Id
																		] || []
																	).length === 0
																}
															>
																<div className="col-xs-12 no-officers text-center text-muted">
																	{lang.t(
																		"No applicants received",
																	)}
																</div>
															</If>
															<If
																condition={
																	!isActiveOrCompletedJob &&
																	allowAssign
																}
															>
																<div className="col-xs-12 no-officers text-center">
																	<If
																		condition={
																			(
																				applicantList[
																					position.Id
																				] || []
																			).length === 0
																		}
																	>
																		<button
																			type="button"
																			className="btn btn-primary"
																			onClick={() =>
																				this.openManualAssignModal(
																					position.Id,
																				)
																			}
																		>
																			{lang.t(
																				"Manually Assign Officers",
																			)}
																		</button>
																	</If>
																</div>
															</If>
														</When>
														<When
															condition={
																positionManager[position.Id]
																	.showSeeking &&
																!positionManager[position.Id]
																	.showAssigned
															}
														>
															<For
																index="index"
																each="officer"
																of={(
																	assignedOfficersList[
																		position.Id
																	] || []
																).filter(
																	officer =>
																		officer.OfficerStatus ===
																		seekingReplacement.type,
																)}
															>
																<div
																	className="col-xs-12"
																	key={`assigned_officer_${index}`}
																>
																	<div className="row position-officer">
																		<div className="col-xs-4 col-sm-3">
																			<Image
																				className="profile-img img-circle"
																				src={
																					officer
																						.OfficerProfile
																						?.ContactPhotoUrl ||
																					""
																				}
																				fallbackSrc="avatar"
																			/>
																			<If
																				condition={
																					officer.RecurrenceRuleJobAssignment &&
																					officer.RecurrenceRuleJobAssignment !==
																						null
																				}
																			>
																				<Tooltip content="This officer is assigned to multiple occurrences of this job">
																					<i className="rkf rkf-refresh recurring-icon" />
																				</Tooltip>
																			</If>
																		</div>
																		<div className="col-xs-8 col-sm-6">
																			<div className="row">
																				<div className="col-sm-12">
																					<strong className="text-capitalize">
																						{officer
																							.OfficerProfile
																							?.FirstName ||
																							""}
																						&nbsp;
																						{officer
																							.OfficerProfile
																							?.LastName ||
																							""}
																					</strong>
																				</div>
																			</div>
																			<div className="row">
																				<div className="col-sm-12">
																					{officer
																						.OfficerProfile
																						?.OfficerRank ||
																						""}
																					{officer
																						.OfficerProfile
																						?.OfficerRank &&
																					officer
																						.OfficerProfile
																						?.JurisdictionName
																						? ", "
																						: ""}
																					{officer
																						.OfficerProfile
																						?.JurisdictionName ||
																						""}
																				</div>
																			</div>
																			<div className="row">
																				<If
																					condition={
																						officer
																							.OfficerProfile
																							?.CellPhone
																					}
																				>
																					<div className="col-sm-12">
																						<i className="rkf rkf-phone" />
																						&nbsp;
																						{utils.formatPhoneNumber(
																							officer
																								.OfficerProfile
																								.CellPhone ||
																								"",
																						)}
																					</div>
																				</If>
																				<If
																					condition={
																						officer
																							.OfficerProfile
																							?.Email
																					}
																				>
																					<div
																						className="col-sm-12 truncate"
																						title={
																							officer
																								.OfficerProfile
																								.Email
																						}
																					>
																						<i className="rkf rkf-email" />
																						&nbsp;
																						{officer
																							.OfficerProfile
																							.Email ||
																							""}
																					</div>
																				</If>
																			</div>
																		</div>
																		<div className="col-xs-12 col-sm-3">
																			<div className="row">
																				<div className="col-xs-6 col-sm-12">
																					<If
																						condition={
																							allowUnassign &&
																							!officer.ClockIn
																						}
																					>
																						<button
																							type="button"
																							className="btn btn-danger btn-block"
																							onClick={this.onChangeApplicantStatus(
																								officer,
																								position.Id,
																								true,
																							)}
																						>
																							{lang.t(
																								"Unassign",
																							)}
																						</button>
																					</If>
																				</div>
																			</div>
																		</div>
																	</div>
																</div>
															</For>
															<If
																condition={
																	(
																		assignedOfficersList[
																			position.Id
																		] || []
																	).filter(
																		officer =>
																			officer.OfficerStatus ===
																			seekingReplacement.type,
																	).length === 0
																}
															>
																<div className="col-xs-12 no-officers text-center text-muted">
																	{lang.t(
																		"No officers seeking replacement",
																	)}
																</div>
															</If>
															<If condition={allowAssign}>
																<div className="col-xs-12 no-officers text-center">
																	<button
																		type="button"
																		className="btn btn-primary"
																		onClick={() =>
																			this.openManualAssignModal(
																				position.Id,
																			)
																		}
																	>
																		{lang.t(
																			"Manually Assign Officers",
																		)}
																	</button>
																</div>
															</If>
														</When>
													</Choose>
													<If
														condition={this.positionHasRequirements(
															position.Id,
														)}
													>
														<div className="col-xs-12">
															<button
																type="button"
																className="btn btn-link btn-block"
																onClick={() =>
																	this.togglePositionDetails(
																		position.Id,
																	)
																}
															>
																<i
																	className={classNames("rkf", {
																		"rkf-arrow-down":
																			!positionManager[
																				position.Id
																			].showDetails,
																		"rkf-arrow-up":
																			positionManager[
																				position.Id
																			].showDetails,
																	})}
																/>
																&nbsp;
																{positionManager[position.Id]
																	.showDetails
																	? lang.t("Hide")
																	: lang.t("Show")}
																&nbsp;
																{lang.t(
																	"position details and instructions",
																)}
															</button>
														</div>
													</If>
													<div
														className={`col-xs-12 collapsing-panel${positionManager[position.Id].showDetails ? " expanded" : ""}`}
													>
														<If condition={position.Details}>
															<div className="text-muted">
																{position.Details}
															</div>
														</If>
														<div>
															<strong>{`${lang.t("Requirements")}:`}</strong>
															<ul>
																<If
																	condition={
																		position.RequiredJurisdictionAssets &&
																		position
																			.RequiredJurisdictionAssets
																			.length > 0
																	}
																>
																	<li>{`${lang.t("Assets / Equipment")}`}</li>
																	<ul>
																		<For
																			index="index"
																			each="asset"
																			of={
																				position.RequiredJurisdictionAssets
																			}
																		>
																			<li
																				key={`asset_${asset.Id}`}
																			>
																				{asset.Description}
																			</li>
																		</For>
																	</ul>
																</If>
																<If
																	condition={
																		position.VehicleRequired &&
																		position.VehicleTypes
																			.length > 0
																	}
																>
																	<li>{`${lang.t("Vehicle")}`}</li>
																	<ul>
																		<For
																			index="index"
																			each="vehicle"
																			of={
																				position.VehicleTypes
																			}
																		>
																			<li
																				key={`vehicle_${vehicle.Id}`}
																			>
																				{vehicle.Name}
																			</li>
																		</For>
																	</ul>
																</If>
																<If
																	condition={
																		position.DressTypeName
																	}
																>
																	<li>
																		{`${lang.t("Dress Type")}: `}
																		{position.DressTypeName}
																		<If
																			condition={
																				position.DressTypeNote
																			}
																		>
																			<ul>
																				<li>
																					{`${lang.t("Notes")}: `}
																					{
																						position.DressTypeNote
																					}
																				</li>
																			</ul>
																		</If>
																	</li>
																</If>
																<If
																	condition={
																		position.RequestedOfficerRank
																	}
																>
																	<li>
																		{lang.t(
																			position.OfficerRankRequired
																				? "Specific Rank"
																				: "Minimum Rank",
																		)}
																		:{" "}
																		{
																			position.RequestedOfficerRank
																		}
																	</li>
																</If>
																<If
																	condition={
																		position.MaxRequestedOfficerRank
																	}
																>
																	<li>
																		{lang.t("Maximum rank")}:{" "}
																		{
																			position.MaxRequestedOfficerRank
																		}
																	</li>
																</If>
																<If
																	condition={
																		position.JobCertifications &&
																		position.JobCertifications
																			.length > 0
																	}
																>
																	<li>
																		{`${lang.t("Certifications")}: `}
																		{this.renderCertifications(
																			position.Id,
																		)}
																	</li>
																</If>
																<If
																	condition={
																		position.RovingPatrol
																	}
																>
																	<li>
																		{`${lang.t("Roving Patrol")}: `}
																		{position.RovingPatrol}
																		&nbsp;
																		<span className="text-lowercase">
																			{position.RovingPatrol !==
																			1
																				? lang.t("Miles")
																				: lang.t("Mile")}
																		</span>
																	</li>
																</If>
																<If
																	condition={
																		position.OfficerRequirements
																	}
																>
																	<li>
																		{`${lang.t("Other Requirements")}: `}
																		{
																			position.OfficerRequirements
																		}
																	</li>
																</If>
															</ul>
														</div>
													</div>
												</div>
											</For>
										</For>
									</div>
									{/* Job Positions Panel */}

									{/* Job Contacts Panel */}
									<div className="details-panel">
										<div className="row">
											<div className="col-sm-12">
												<h4>{lang.t("Job Contact")}</h4>
											</div>
										</div>
										<div className="separator separator-solid" />
										<If condition={jobEvent.ClientSupervisorName}>
											<div className="row">
												<div className="col-xs-6 col-sm-4">{`${lang.t("Client Contact")}:`}</div>
												<div className="col-xs-6 col-sm-8 text-muted">
													{jobEvent.ClientSupervisorName}
													<br />
													{utils.formatPhoneNumber(
														jobEvent.ClientSupervisorMobilePhone || "",
													)}
												</div>
											</div>
											<br />
										</If>
										<div className="row">
											<div className="col-xs-6 col-sm-4">{`${lang.t("Agency Contact")}:`}</div>
											<div className="col-xs-6 col-sm-8 text-muted">
												{jobEvent.SupervisorName || lang.t("Unspecified")}
												<br />
												{utils.formatPhoneNumber(
													jobEvent.SupervisorMobilePhone,
												) || ""}
											</div>
										</div>
										<If
											condition={
												(jobEvent.SecondarySupervisorModel || []).length > 0
											}
										>
											<br />
											<div className="row">
												<div className="col-xs-6 col-sm-4">{`${lang.t("Additional Agency Contact")}:`}</div>
												<div className="col-xs-6 col-sm-8 text-muted">
													{this.renderAdditionalContacts()}
												</div>
											</div>
										</If>
									</div>
									{/* Job Contacts Panel */}

									<If
										condition={
											(RequiredJurisdictions || []).length > 0 ||
											(ExcludedJurisdictions || []).length > 0
										}
									>
										{/* Job Requirements Panel */}
										<div className="details-panel">
											<div className="row">
												<div className="col-sm-12">
													<h4>{lang.t("Applicant Restrictions")}</h4>
												</div>
											</div>
											<div className="separator separator-solid" />
											<div className="row">
												<div className="col-sm-12">
													{lang.t("Show only to")}:
												</div>
												<ul>
													<For
														each="required"
														of={RequiredJurisdictions}
													>
														<li
															key={`required-restriction-${required.JurisdictionId}`}
															className="text-muted"
														>
															{this.renderJurisdictionRestriction(
																required,
															)}
														</li>
													</For>
												</ul>
											</div>
											<If condition={ExcludedJurisdictions.length > 0}>
												<br
													className={
														RequiredJurisdictions.length === 0
															? "hidden"
															: undefined
													}
												/>
												<div className="row">
													<div className="col-sm-12">
														{lang.t("Hide from")}:
													</div>
													<ul>
														<For
															each="excluded"
															of={ExcludedJurisdictions}
														>
															<li
																key={`excluded-restriction-${excluded.JurisdictionId}`}
																className="text-muted"
															>
																{this.renderJurisdictionRestriction(
																	excluded,
																)}
															</li>
														</For>
													</ul>
												</div>
											</If>
										</div>
										{/* Job Requirements Panel */}
									</If>

									{/* Payment Details Panel */}
									<div className="details-panel">
										<div className="row">
											<div className="col-sm-12">
												<h4>{lang.t("Payment Details")}</h4>
											</div>
										</div>
										<div className="separator separator-solid" />
										<div className="row">
											<Choose>
												<When condition={isInvoicingV3}>
													<If
														condition={
															jobEvent.JobInvoicedTypeId ===
															JOB_INVOICED_TYPES.OFFICER_INVOICING
														}
													>
														<div className="col-sm-12">
															{lang.t("Invoiced by Officer")}
														</div>
													</If>
													<If
														condition={
															jobEvent.JobInvoicedTypeId ===
															JOB_INVOICED_TYPES.AGENCY_INVOICING
														}
													>
														<div className="col-sm-12">
															{lang.t("Invoiced by Agency")}
														</div>
													</If>
													<If
														condition={
															jobEvent.JobInvoicedTypeId ===
															JOB_INVOICED_TYPES.SCHEDULE_ONLY
														}
													>
														<div className="col-sm-12">
															{lang.t("Scheduling Only")}
														</div>
													</If>
												</When>
												<Otherwise>
													<div className="col-xs-6 col-sm-3">{`${lang.t("RKPay Enabled")}:`}</div>
													<div className="col-xs-6 col-sm-9 text-muted">
														{jobEvent.IsRKPay ||
														jobEvent.JobInvoicedTypeId === 1
															? lang.t("Yes")
															: lang.t("No")}
													</div>
												</Otherwise>
											</Choose>
										</div>
										<If condition={jobEvent.RequirePrepayment}>
											<div className="row">
												<div className="col-xs-6 col-sm-3">{`${lang.t("Requires Prepayment")}:`}</div>
												<div className="col-xs-6 col-sm-9 text-muted">
													{lang.t("Yes")}
												</div>
											</div>
										</If>
										<If condition={!isInvoicingV3}>
											<div className="row">
												<div className="col-xs-6 col-sm-3">{`${lang.t("Payment Details")}:`}</div>
												<div className="col-xs-6 col-sm-9 text-muted">
													{jobEvent.IsRKPay
														? "via RKPay"
														: jobEvent.PaymentMethod}
												</div>
											</div>
										</If>
									</div>
									{/* Payment Details Panel */}
								</div>

								<div className="col-md-4">
									<If condition={isActiveJob && allowChat}>
										<button
											type="button"
											className="btn btn-primary btn-block"
											onClick={() => this.openJobChat()}
										>
											Open Job Chat
										</button>
									</If>
									{/* Map & Notes Panel */}
									<div className="details-panel">
										<div className="row">
											<div className="col-xs-2 text-muted detail-icon">
												<i className="rkf rkf-map-pin" />
											</div>
											<div className="col-xs-10">
												<If condition={!!jobEvent.LocationName}>
													<strong>{jobEvent.LocationName}</strong>
												</If>
												<p className="text-muted">
													{this.renderLocationDetails()}
												</p>
												<If
													condition={
														!!jobEvent.LocationZoneName &&
														user.Organization.UseZones
													}
												>
													<strong>{lang.t("Location Zone")}</strong>
													<p className="text-muted">
														{jobEvent.LocationZoneName}
													</p>
												</If>
											</div>
										</div>
										<div className="row map-container">
											<GoogleMap
												defaultZoom={15}
												center={{
													lat: parseFloat(
														jobEvent?.LocationLatitude ||
															GOOGLE_MAP_SETTINGS.defaultCenter.lat,
													),
													lng: parseFloat(
														jobEvent?.LocationLongitude ||
															GOOGLE_MAP_SETTINGS.defaultCenter.lng,
													),
												}}
												bootstrapURLKeys={{ key: GOOGLE_MAP_SETTINGS.key }}
												options={GOOGLE_MAP_OPTIONS}
											>
												<MapMarker
													lat={parseFloat(
														jobEvent?.LocationLatitude ||
															GOOGLE_MAP_SETTINGS.defaultCenter.lat,
													)}
													lng={parseFloat(
														jobEvent?.LocationLongitude ||
															GOOGLE_MAP_SETTINGS.defaultCenter.lng,
													)}
												/>
											</GoogleMap>
										</div>
										<div
											role="button"
											aria-hidden="true"
											name="toggle-notes"
											className="row clickable-row"
											onClick={e => this.toggleNotes(e)}
										>
											<div
												className={`${
													jobEventNotes.length !== 0
														? "col-xs-8 col-sm-10 col-md-4"
														: "col-xs-12"
												}`}
											>
												<strong>{lang.t("Notes")}</strong>
												<i
													className={classNames("glyphicon", {
														"glyphicon-menu-down": !showNotes,
														"glyphicon-menu-up": showNotes,
													})}
												/>
											</div>
											<If condition={jobEventNotes.length !== 0}>
												<div className="col-xs-2 col-md-8 text-right">
													<button
														type="button"
														name="view-notes"
														className="btn btn-primary btn-sm view-notes"
														onClick={() => this.toggleViewNoteModal()}
													>
														{lang.t("View History")}
													</button>
												</div>
											</If>
										</div>
										<div
											className={`row collapsing-panel${showNotes ? " expanded" : ""}`}
										>
											<If condition={notesLoading}>
												<div className="col-xs-12 text-center loader-overflow">
													<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
												</div>
											</If>
											<Choose>
												<When
													condition={
														jobEventNotes.length === 0 && !notesLoading
													}
												>
													<div className="col-xs-12 text-center text-muted">
														{lang.t("No notes")}
													</div>
												</When>
												<Otherwise>
													<ul className="notes-list">
														<For
															each="note"
															of={jobEventNotes}
														>
															<li key={`note_${note.Id}`}>
																<div className="note-header">
																	<div>
																		{note.UserName}
																		&nbsp;-&nbsp;
																		<small className="text-muted">
																			{utils.formatDate(
																				`${note.CreateDateUTC}Z`,
																				"en-US",
																				userTimeZone,
																				user.UserPreferences ===
																					"useMilitaryTime"
																					? "dt-num-mil"
																					: "dt-num",
																			)}
																		</small>
																	</div>
																	<If
																		condition={
																			user.IsStaffAdmin &&
																			allowAddNotes
																		}
																	>
																		<div>
																			<button
																				type="button"
																				className="btn btn-link btn-block text-right"
																				onClick={() =>
																					this.handleDeleteJobEventNote(
																						note.Id,
																					)
																				}
																			>
																				<i className="glyphicon glyphicon-trash text-danger" />
																			</button>
																		</div>
																	</If>
																</div>
																<p>{note.Comment}</p>
																<If condition={note.Url}>
																	<Image
																		className="img-responsive"
																		src={note.Url}
																		fallbackSrc="default"
																	/>
																</If>
															</li>
														</For>
													</ul>
												</Otherwise>
											</Choose>
										</div>
									</div>
									{/* Map & Notes Panel */}
								</div>
							</div>
						</div>
						{/* Job Details Container */}
						<ConfirmationModal
							ref={ref => {
								this._confirmationModal = ref;
							}}
						/>
						<DeleteJobConfirmationModal
							ref={ref => {
								this._deleteJobConfirmationModal = ref;
							}}
							modalDetails={deleteJobConfirmationModal}
							onHide={() =>
								this.setState({
									deleteJobConfirmationModal: { show: false, deleteAll: false },
								})
							}
							handleDeleteJob={this.handleDeleteJob}
						/>
						<SeriesConfirmationModal
							show={seriesConfirmationModal.show}
							actionType={seriesConfirmationModal.actionType}
							onConfirm={seriesConfirmationModal.onConfirm}
							onClose={this.closeSeriesConfirmationModal}
						/>
						<JobHistoryModal
							show={showJobHistoryModal}
							timeZoneId={userTimeZone}
							jobEventId={jobEvent.Id}
							onClose={this.toggleJobHistoryModal}
							user={user}
						/>
						<AddNoteModal
							show={showAddNoteModal}
							onCancel={this.toggleAddNoteModal}
							jobEventId={jobEvent.Id}
							locationId={jobEvent.LocationId}
							getJobEventNotes={this.getJobEventNotes}
						/>
						<ManualAssignModal
							show={manualAssignModal.show}
							defaultPosition={manualAssignModal.positionId}
							isActiveJob={isActiveJob}
							timeZoneId={userTimeZone}
							jurisdictionId={user.Organization.JurisdictionId}
							isSuperAdmin={user.isSuperAdmin()}
							isLEA={user.isLEA()}
							isStaffAdmin={!!user?.IsStaffAdmin}
							positions={jobEvent.Jobs || []}
							recurrenceRule={jobEvent.JobEventRecurrenceRule}
							isAgencyW2={user.Organization.TransferOfficerPaymentToJurisdiction}
							isPrepayJob={jobEvent.RequirePrepayment}
							isRKPayJob={jobEvent.IsRKPay}
							successCallback={() => {
								this.receiveJobEvent(jobEvent.Id);
								this.retrieveOfficersForPosition(manualAssignModal.positionId);
							}}
							closeModal={() =>
								this.setState({
									manualAssignModal: { show: false, positionId: undefined },
								})
							}
							isViewOnly={user.IsViewOnly}
						/>
						<UnassignOfficerModal
							positionId={unassignPositionId}
							unassignProps={unassignModal}
							onClose={this.closeUnassignmentModal}
						/>
						<ViewNotesModal
							show={showViewNotesModal}
							onClose={this.toggleViewNoteModal}
							timeZoneId={user.TimeZoneId}
							jobLocation={jobEvent.LocationName}
							jobEventId={jobEvent.Id}
							user={user}
						/>
						<EditTimecardModal
							show={showEditTimecardModal}
							onHide={this.toggleTimeCardModal}
							onSave={this.updateTimecard}
							positionId={currentPositionEditId}
							officer={currentOfficerEdit}
							clockInTime={clockinTimecardModal}
							clockOutTime={clockoutTimecardModal}
							notes={notesTimecardModal}
							user={user}
							allowEditTimeCard={allowEditTimeCard}
						/>
						<PostJobModal
							show={postJobModal.show}
							updateAllUpcoming={postJobModal.updateAllUpcoming}
							selectedJobs={[jobEvent]}
							closeModal={() =>
								this.setState({
									postJobModal: { show: false, updateAllUpcoming: false },
								})
							}
							organization={user.Organization}
							user={user}
							successCallback={() => this.receiveJobEvent(jobEvent.Id)}
						/>
						<AddToEstimateModal
							show={showAddToEstimateModal}
							onHide={() => this.setState({ showAddToEstimateModal: false })}
							jobs={[jobEvent]}
							user={user}
							onCreateNewEstimate={this.createEstimateForJob}
							handleAddToEstimate={this.handleAddToEstimate}
						/>
						<AddToInvoiceModal
							show={showAddToInvoiceModal}
							onHide={() => this.setState({ showAddToInvoiceModal: false })}
							router={router.push}
							jobs={[jobEvent]}
							user={user}
							onCreateNewInvoice={this.createInvoiceForJob}
						/>
						<ConnectedInvoicesModal
							show={showConnectedInvoicesModal}
							onHide={() => this.setState({ showConnectedInvoicesModal: false })}
							invoices={associatedNonRKPayInvoices}
							router={router}
						/>
						<JobActivityLogModal
							onHide={this.toggleJobActivityLogModal}
							show={showJobActivityLogModal}
							onSave={this.updateTimecard}
							officer={currentOfficerView}
							clockInTime={currentOfficerClockIn}
							clockOutTime={currentOfficerClockOut}
							user={user}
							positionId={currentPositionView?.Id}
							jobEventTimeZoneId={jobEvent.TimeZoneId}
							positionTitle={currentPositionView?.Title}
						/>
						<Loader active={isLoading} />
					</div>
				</Otherwise>
			</Choose>
		);
	}
}

JobDetails.propTypes = {
	params: PropTypes.object,
	user: PropTypes.object,
	router: PropTypes.object,
	initializeChat: PropTypes.func,
	flags: PropTypes.object.isRequired,
};

JobDetails.defaultProps = {
	params: {},
	user: {},
	router: {},
	initializeChat: () => {},
};

export default withLDConsumer()(JobDetails);
